
/**
 * TradeJournal.jsx — Trade Journal with server-side persistence
 * Parses IBKR PDFs, detects spreads, syncs to Cloudflare KV.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { journalGetTrades, journalPutTrades, journalGetRecords, journalPutRecords, journalGetRegimes, journalPutRegimes, journalGetFilter, journalPutFilter, regimeDailyGet, sessionLogGet, sessionLogPut, ibkrSaveCredentials, ibkrGetStatus, ibkrFetchReport, ibkrFetchConfReport, journalGetSettings, journalPutSettings } from '../../lib/api';

async function ensurePdfJs() {
  if (window.pdfjsLib) return window.pdfjsLib;
  await new Promise((res, rej) => {
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    s.onload = res; s.onerror = rej; document.head.appendChild(s);
  });
  window.pdfjsLib.GlobalWorkerOptions.workerSrc =
    'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  return window.pdfjsLib;
}

async function ensureChartJs() {
  if (window.Chart) return window.Chart;
  await new Promise((res, rej) => {
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.min.js';
    s.onload = res; s.onerror = rej; document.head.appendChild(s);
  });
  return window.Chart;
}

// ── HTM Parser (IBKR Trade Confirmation .htm/.html) ──────────────────────────
// IBKR's HTM report is the preferred import format:
//   • Columns are proper <td> elements — no regex guessing
//   • Comm+fee always present on net total rows (even multi-day reports)
//   • Trade dates in their own cells — zero risk of "Generated:" timestamp pollution
//   • No pdf.js dependency — parsed synchronously with DOMParser
//
// Structure:
//   tr.row-summary  → aggregate execution (exchange="-"), has all columns incl. comm+fee
//   tbody.row-detail → named exchange fills (CBOE, ISE…) — used for validation only
//   tr.subtotal     → "Total SYMBOL OCC (Bought|Sold)" or "Total SYMBOL OCC" (net, qty=0)
//
// We read from tr.subtotal rows only for P&L + comm + fee (most reliable source).
// Trade dates come from tr.row-summary cells (column index 2 = trade date).

function parseHTM(html, logFn) {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  // ── Account info ──────────────────────────────────────────────────────────
  let account = 'UNKNOWN', name = 'Unknown';
  doc.querySelectorAll('table tr').forEach(tr => {
    const cells = tr.querySelectorAll('td');
    if (cells.length >= 2) {
      const label = cells[0].textContent.trim();
      const val   = cells[1].textContent.trim();
      if (label === 'Account') account = val;
      if (label === 'Name')    name    = val;
    }
  });
  logFn(`Account: ${account} · ${name}`);

  // ── Build contractData and trade dates from row-summary rows ──────────────
  // Columns (0-based, after the checkbox/icon cell):
  //   0: acct+icon  1+2: symbol (colspan=2 counts as 1 cell in DOM)
  //   The real column indices in the DOM are:
  //   [0]=acct [1]=symbol [2]=date [3]=settle [4]=exchange [5]=type
  //   [6]=qty  [7]=price  [8]=proceeds [9]=comm [10]=fee
  const contractData   = {}; // OCC → { bought?, sold? }
  const contractDates  = {}; // OCC → { open, close }
  const contractComm   = {}; // OCC → total comm+fee (from net subtotal rows)

  // Parse all subtotal rows for P&L and commission
  doc.querySelectorAll('tr.subtotal').forEach(tr => {
    const cells = [...tr.querySelectorAll('td')];
    if (cells.length < 3) return;

    const label     = cells[0].textContent.trim().replace(/\s+/g, ' ');
    const qtyText   = cells[1]?.textContent.trim();
    const qty       = parseInt(qtyText) || 0;

    // "Total NVDA  260522C00215000 (Bought)" or "Total NVDA  260522C00215000 (Sold)"
    const dirMatch  = label.match(/Total\s+(\S+\s+\S+)\s+\((Bought|Sold)\)/);
    // "Total NVDA  260522C00215000"  (net row, qty=0)
    const netMatch  = label.match(/Total\s+(\S+\s+\S+)$/) && qty === 0;

    const procVal   = parseFloat(cells[2]?.textContent.replace(/,/g,'')) || 0;
    const avgPrVal  = parseFloat(cells[3]?.textContent.replace(/,/g,'')) || 0; // note: subtotal cols shift

    // Net total row: "Total UNDERLYING  OCCCODE" with qty=0
    // columns: [0]=label(colspan7) [1]=qty=0 [2]=avgprice(blank) [3]=proceeds [4]=comm [5]=fee
    if (qty === 0 && !dirMatch) {
      const occMatch = label.match(/Total\s+(\S+\s+\S+)/);
      if (!occMatch) return;
      const occ  = occMatch[1].trim();
      // Columns shift because label has colspan=7:
      // [0]=label [1]=qty [2]=avgprice [3]=proceeds [4]=comm [5]=fee
      const pnl  = parseFloat(cells[3]?.textContent.replace(/,/g,'')) || 0;
      const comm = parseFloat(cells[4]?.textContent.replace(/,/g,'')) || 0;
      const fee  = parseFloat(cells[5]?.textContent.replace(/,/g,'')) || 0;
      contractComm[occ] = (comm < 0 ? comm : 0) + (fee < 0 ? fee : 0);
      if (!contractData[occ]) contractData[occ] = {};
      contractData[occ]._netPnl = pnl;
      return;
    }

    // Directional total row: "Total UNDERLYING  OCCCODE (Bought|Sold)"
    if (dirMatch) {
      const occ = dirMatch[1].trim(); // "NVDA  260522C00215000"
      const dir = dirMatch[2].toLowerCase();
      // columns: [0]=label(colspan7) [1]=qty [2]=avgprice [3]=proceeds [4]=comm [5]=fee
      const absQty = Math.abs(parseInt(cells[1]?.textContent) || 0);
      const avg    = parseFloat(cells[2]?.textContent.replace(/,/g,'')) || 0;
      const proc   = parseFloat(cells[3]?.textContent.replace(/,/g,'')) || 0;
      if (!contractData[occ]) contractData[occ] = {};
      contractData[occ][dir] = { qty: absQty, avgPrice: avg, proceeds: proc };
    }
  });

  // ── Collect trade dates + opening direction per OCC from row-summary rows ──
  // row-summary cols: [0]=acct [1]=symbol [2]=date [3]=settle [4]=exch [5]=side(BUY/SELL)
  //                  [6]=qty [7]=price [8]=proceeds ...
  // To find opening direction we sort all trades per symbol by datetime and use the first.
  const contractAllTrades = {}; // ibkrSym → [{datetime, side}]
  doc.querySelectorAll('tr.row-summary').forEach(tr => {
    const cells = [...tr.querySelectorAll('td')];
    if (cells.length < 7) return;
    const symbolText = cells[1]?.textContent.trim();
    const dateText   = cells[2]?.textContent.trim(); // "2026-05-22, 10:53:16"
    if (!dateText || !symbolText) return;
    const dateOnly   = dateText.split(',')[0].trim(); // "2026-05-22"
    const side       = cells[5]?.textContent.trim();   // "BUY" or "SELL"

    if (!contractAllTrades[symbolText]) contractAllTrades[symbolText] = [];
    contractAllTrades[symbolText].push({ datetime: dateText, side });

    if (!contractDates[symbolText]) contractDates[symbolText] = [];
    contractDates[symbolText].push(dateOnly);
  });

  // Build contractFirstQty from chronologically sorted trades
  const contractFirstQty = {}; // ibkrSym → 'SELL'|'BUY' (opening trade side)
  for (const [sym, trades] of Object.entries(contractAllTrades)) {
    const sorted = trades.slice().sort((a, b) => a.datetime.localeCompare(b.datetime));
    if (sorted.length > 0) contractFirstQty[sym] = sorted[0].side; // "BUY" or "SELL"
  }

  // ── Build raw legs ─────────────────────────────────────────────────────────
  let contractCount = 0, closedCount = 0;
  const rawLegs = [];

  for (const [occ, data] of Object.entries(contractData)) {
    contractCount++;
    if (data._netPnl === undefined) continue; // no net total row = open position
    closedCount++;

    const pnl    = data._netPnl;
    const parsed = parseContractFromOCC(occ);
    if (!parsed) continue;

    // Find dates from the IBKR symbol format
    const ibkrSym = occToIBKR(occ);
    const dates   = contractDates[ibkrSym] || [];
    const sorted  = [...new Set(dates)].sort();
    const open    = sorted[0]  || '?';
    const close   = sorted[sorted.length - 1] || '?';

    // Opening direction: first trade qty negative = sold to open (short leg)
    //                    first trade qty positive = bought to open (long leg)
    const firstSide   = contractFirstQty[ibkrSym];    // 'BUY' | 'SELL' | undefined
    const openedShort = firstSide === 'SELL' ? true : firstSide === 'BUY' ? false : null;

    rawLegs.push({
      key:        `${account}_${occ}_${open}_${close}`,
      account, contract: occ,
      underlying: parsed.underlying,
      type:       parsed.type,
      strike:     parsed.strike,
      expiry:     parsed.expiry,
      tradeDate:  open,
      closeDate:  close,
      boughtQty:  data.bought?.qty  || 0,
      soldQty:    data.sold?.qty    || 0,
      boughtAvg:  data.bought?.avgPrice || 0,
      soldAvg:    data.sold?.avgPrice   || 0,
      grossPnl:   pnl,
      commPnl:    contractComm[occ] || 0,   // commission+fees for this leg (≤ 0)
      isWin:      pnl > 0,
      multiplier: parsed.type === 'STOCK' ? 1 : 100,
      openedShort,
    });
  }

  const totalComm = Object.values(contractComm).reduce((s, v) => s + v, 0);
  logFn(`   Total commissions+fees: $${totalComm.toFixed(2)}`);
  logFn(`${contractCount} contracts · ${closedCount} closed`);
  logFn(`${rawLegs.length} raw legs → detecting spreads…`);
  const trades = detectSpreads(rawLegs);
  logFn(`${trades.filter(t=>t.isSpread).length} spreads + ${trades.filter(t=>!t.isSpread).length} singles`);
  return { account, name, trades, totalComm };
}

// Convert OCC code "260522C00215000" format to parsed contract info
// OCC code examples: 260522C00215000, 260522P00217500, 260630P00713000
function parseContractFromOCC(occ) {
  // occ is the full OCC code like "NVDA 260522C00215000" or standalone "260522C00215000"
  // We get the raw OCC from the HTM label which is "NVDA  260522C00215000"
  // Split to get underlying + code
  const parts = occ.trim().split(/\s+/);
  if (parts.length < 2) return null;
  const underlying = parts[0];
  const code       = parts[1] || parts[0]; // fallback if no space

  // Parse the OCC code: YYMMDD + C/P + 8-digit strike (×1000)
  const m = code.match(/^(\d{2})(\d{2})(\d{2})([CP])(\d{8})$/);
  if (!m) return null;
  const [, yy, mm, dd, cp, strikeRaw] = m;
  const type   = cp === 'C' ? 'CALL' : 'PUT';
  const strike = parseInt(strikeRaw) / 1000;
  const expiry = `20${yy}-${mm}-${dd}`;
  return { underlying, type, strike, expiry };
}

// Convert OCC like "NVDA  260522C00215000" to IBKR display format "NVDA 22MAY26 215 C"
function occToIBKR(occ) {
  const parts = occ.trim().split(/\s+/);
  if (parts.length < 2) return occ;
  const underlying = parts[0];
  const code       = parts[1];
  const m = code.match(/^(\d{2})(\d{2})(\d{2})([CP])(\d{8})$/);
  if (!m) return occ;
  const [, yy, mm, dd, cp, strikeRaw] = m;
  const months = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
  const month  = months[parseInt(mm) - 1];
  const day    = dd; // keep leading zero — IBKR displays "01JUN26", not "1JUN26"
  const strike = (parseInt(strikeRaw) / 1000);
  const strikeStr = Number.isInteger(strike) ? strike.toString() : strike.toString();
  return `${underlying} ${day}${month}${yy} ${strikeStr} ${cp}`;
}

function parseContract(raw) {
  const opt = raw.match(/^(\w+)\s+(\d{6})([CP])(\d+)$/);
  if (opt) {
    const [, sym, exp, t, sr] = opt;
    return { underlying: sym, type: t === 'C' ? 'CALL' : 'PUT',
      strike: parseInt(sr) / 1000,
      expiry: `20${exp.slice(0,2)}-${exp.slice(2,4)}-${exp.slice(4,6)}` };
  }
  const stock = raw.replace(/\s*\(.*\)/, '').trim().match(/^([A-Z]{1,5})$/);
  if (stock) return { underlying: stock[1], type: 'STOCK', strike: null, expiry: null };
  return null;
}

// ── Spread detection ─────────────────────────────────────────────────────────
// ── Spread classification ─────────────────────────────────────────────────────
//
// Uses both strikes AND direction (boughtQty vs soldQty) to classify correctly.
//
// Vertical spread = same type (both P or both C), OPPOSITE directions (one long + one short)
// Strangle/Straddle = different types, SAME direction (both short or both long)
// Singles = same type, SAME direction → two separate trades, NOT a spread
//
// openedShort=true means first trade was a sell (short leg); false=long; null=unknown fallback.
function isShortLeg(leg) {
  if (leg.openedShort === true)  return true;
  if (leg.openedShort === false) return false;
  return (leg.soldQty || 0) > (leg.boughtQty || 0);
}
function isLongLeg(leg) {
  if (leg.openedShort === false) return true;
  if (leg.openedShort === true)  return false;
  return (leg.boughtQty || 0) > (leg.soldQty || 0);
}

function classifySpread(a, b) {
  const lo = Math.min(a.strike, b.strike);
  const hi = Math.max(a.strike, b.strike);
  const sameType = a.type === b.type;

  if (sameType) {
    // Vertical: one long + one short, same type
    const higherLeg = a.strike >= b.strike ? a : b;
    const typeChar = a.type === 'CALL' ? 'C' : 'P';
    const dirLabel = a.type === 'PUT'
      ? (isShortLeg(higherLeg) ? 'Bull Put' : 'Bear Put')
      : (isShortLeg(higherLeg) ? 'Bear Call' : 'Bull Call');
    return `${lo}/${hi}${typeChar} Vert (${dirLabel})`;
  }

  // Mixed types (P + C)
  const call = a.type === 'CALL' ? a : b;
  const put  = a.type === 'PUT'  ? a : b;
  const bothShort = isShortLeg(call) && isShortLeg(put);
  const bothLong  = isLongLeg(call)  && isLongLeg(put);
  const dir = bothShort ? 'Short ' : bothLong ? 'Long ' : '';

  if (call.strike === put.strike) return `${lo} ${dir}Straddle`;
  if (call.strike > put.strike)  return `${put.strike}P/${call.strike}C ${dir}Strangle`;
  return `${put.strike}P/${call.strike}C RR`;
}

// Score how well two legs fit as a spread pair. Returns -1 if invalid pair.
function spreadFitScore(a, b) {
  if (a.underlying !== b.underlying || a.expiry !== b.expiry) return -1;
  const sameType = a.type === b.type;
  const aShort = isShortLeg(a), bShort = isShortLeg(b);
  const aLong  = isLongLeg(a),  bLong  = isLongLeg(b);
  const width  = Math.abs((a.strike || 0) - (b.strike || 0));

  if (sameType) {
    // Vertical: opposite directions required
    if ((aShort && bLong) || (aLong && bShort)) return 1000 - width;
    return -1; // same type + same direction = two separate singles
  }
  // Mixed type: same direction = strangle/straddle
  if ((aShort && bShort) || (aLong && bLong)) return 500 - width;
  return -1; // one long + one short of different types = synthetic (ignore)
}

function detectSpreads(legs) {
  const options = legs.filter(l => l.type !== 'STOCK');
  const stocks  = legs.filter(l => l.type === 'STOCK');
  const groups  = {};
  for (const leg of options) {
    const k = `${leg.underlying}|${leg.expiry}|${leg.tradeDate}`;
    if (!groups[k]) groups[k] = [];
    groups[k].push(leg);
  }
  const result = [];
  for (const groupLegs of Object.values(groups)) {
    const remaining = [...groupLegs];
    const used = new Set();
    // Greedy best-fit: score all pairs, pick highest first
    while (true) {
      let bestScore = -1, bestI = -1, bestJ = -1;
      for (let i = 0; i < remaining.length; i++) {
        if (used.has(i)) continue;
        for (let j = i + 1; j < remaining.length; j++) {
          if (used.has(j)) continue;
          const score = spreadFitScore(remaining[i], remaining[j]);
          if (score > bestScore) { bestScore = score; bestI = i; bestJ = j; }
        }
      }
      if (bestScore < 0) break;
      const leg1 = remaining[bestI], leg2 = remaining[bestJ];
      used.add(bestI); used.add(bestJ);
      const spreadType = classifySpread(leg1, leg2);
      const combinedPnl = (leg1.grossPnl || 0) + (leg2.grossPnl || 0);
      const lo = Math.min(leg1.strike, leg2.strike), hi = Math.max(leg1.strike, leg2.strike);
      result.push({
        key: `${leg1.key}__${leg2.key}`, account: leg1.account,
        contract: `${leg1.underlying} ${leg1.expiry} ${spreadType}`,
        underlying: leg1.underlying, type: 'SPREAD', spreadType,
        strike: null, strikeRange: `${lo}/${hi}`, expiry: leg1.expiry,
        tradeDate: leg1.tradeDate,
        closeDate: [leg1.closeDate, leg2.closeDate].filter(Boolean).sort().pop() || leg1.closeDate,
        boughtQty: Math.max(leg1.boughtQty || 0, leg2.boughtQty || 0),
        soldQty:   Math.max(leg1.soldQty   || 0, leg2.soldQty   || 0),
        boughtAvg: null, soldAvg: null,
        grossPnl: combinedPnl, isWin: combinedPnl > 0,
        commPnl: (leg1.commPnl || 0) + (leg2.commPnl || 0),
        isSpread: true, legs: [leg1, leg2],
      });
    }
    for (let i = 0; i < remaining.length; i++) {
      if (!used.has(i)) {
        const leg = remaining[i];
        result.push({ ...leg, isSpread: false, spreadType: null, legs: [leg] });
      }
    }
  }
  for (const s of stocks) result.push({ ...s, isSpread: false, spreadType: null, legs: [s] });
  return result.sort((a, b) => (b.closeDate || '').localeCompare(a.closeDate || ''));
}

// ── PDF parser ───────────────────────────────────────────────────────────────
function parseOCCtoSymbol(contract) {
  // Convert OCC contract ID "SPY 260519C00733000" to IBKR execution row symbol "SPY 19MAY26 733 C"
  // This is the format used in individual execution rows, not in Total lines
  const parts = contract.split(' ');
  if (parts.length < 2) return null;
  const underlying = parts[0];
  const m = parts[1].match(/^(\d{2})(\d{2})(\d{2})([CP])(\d+)$/);
  if (!m) return null;
  const [, yy, mm, dd, cp, strikeRaw] = m;
  const months = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
  const month = months[parseInt(mm) - 1];
  const day = parseInt(dd).toString();
  const strike = parseInt(strikeRaw) / 1000;
  const strikeStr = Number.isInteger(strike) ? strike.toString() : strike.toString();
  return `${underlying} ${day}${month}${yy} ${strikeStr} ${cp}`;
}

function findTradeDates(text, contract) {
  // Use IBKR symbol format to find execution rows (not OCC code which only appears in Total lines)
  const symbol = parseOCCtoSymbol(contract);
  const searchStr = symbol || contract.split(' ').slice(0,2).join(' ');
  
  // Find all occurrences of this symbol in the text
  const positions = [];
  let pos = 0;
  while ((pos = text.indexOf(searchStr, pos)) >= 0) {
    positions.push(pos);
    pos += searchStr.length;
    if (positions.length > 100) break; // safety cap
  }
  if (!positions.length) return {};
  
  // Take the range from first to last occurrence + 200 chars buffer
  const first = Math.max(0, positions[0] - 50);
  const last  = positions[positions.length - 1] + 200;
  // Strip "Generated: YYYY-MM-DD, HH:MM:SS TZ" lines — these appear as page footers in
  // multi-day IBKR PDFs and contain a timestamp that would otherwise be mistaken for a
  // trade execution date, causing the close date to become the PDF generation date.
  const block = text.slice(first, last).replace(/Generated:\s*\d{4}-\d{2}-\d{2},\s*\d{2}:\d{2}:\d{2}[^\n]*/g, '');
  
  // Only match dates followed by a time (HH:MM:SS) — these are trade execution datetimes.
  // Settle dates appear WITHOUT a trailing time, so this excludes T+1/T+2 settle dates
  // (e.g. 2026-05-26) that would otherwise be picked up as the "close" date for
  // same-day trades, making them appear to close 4 days in the future.
  const dates = []; const re = /(20\d{2}-\d{2}-\d{2}),\s*\d{2}:\d{2}:\d{2}/g; let dm;
  while ((dm = re.exec(block)) !== null) dates.push(dm[1]);
  if (!dates.length) return {};
  dates.sort();
  return { open: dates[0], close: dates[dates.length - 1] };
}

function parsePDF(text, logFn) {
  const acctM = text.match(/Account\s+U(\d+)/);
  const nameM = text.match(/Name\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+)/);
  const account = acctM ? 'U'+acctM[1] : 'UNKNOWN', name = nameM ? nameM[1] : 'Unknown';
  logFn(`Account: ${account} · ${name}`);
  const contractData = {};
  const totalRE = /Total\s+([\w\s.]+?)\s+\((Bought|Sold)\)\s+([-\d.]+)\s+([\d.]+)\s+([-\d,]+\.?\d*)/g;
  const flatRE  = /Total\s+([\w\s.]+?)\s+0\s+([-\d,]+\.?\d*)/g;
  let m;
  while ((m = totalRE.exec(text)) !== null) {
    const [, raw, dir, qty, avgPrice, proceedsRaw] = m;
    const contract = raw.trim().replace(/\s+/g,' ');
    if (!contractData[contract]) contractData[contract] = {};
    contractData[contract][dir.toLowerCase()] = { qty: parseInt(qty), avgPrice: parseFloat(avgPrice), proceeds: parseFloat(proceedsRaw.replace(/,/g,'')) };
  }
  const flatPnls = {};
  while ((m = flatRE.exec(text)) !== null) {
    const contract = m[1].trim().replace(/\s+/g,' ');
    if (contract.length > 4) flatPnls[contract] = parseFloat(m[2].replace(/,/g,''));
  }

  // Extract commissions+fees by summing NAMED exchange rows only (CBOE, ISE, MERCURY, etc.).
  //
  // Why not the "-" summary rows: the "-" exchange character in IBKR PDFs may be a
  // non-ASCII dash (en-dash or PDF-specific glyph) that regex fails to match.
  //
  // Why not "Total X 0 pnl comm fee" lines: multi-day PDFs omit comm/fee from Total lines —
  // only the per-execution rows carry those columns. The Total-line approach only worked
  // for single-day PDFs.
  //
  // Named exchange rows are always present, always use ASCII names, and summing them
  // gives exact total commissions. The "-" summary rows are excluded by requiring \w+
  // (word chars only) for the exchange column — a plain hyphen/dash is not a word char.
  //
  // Row format: U\d+ SYMBOL DATE,TIME SETTLE EXCHANGE TYPE QTY PRICE PROCEEDS COMM FEE ...
  // Example:    U6019535 NVDA 22MAY26 217.5 C 2026-05-19, 10:04:28 2026-05-20 MERCURY BUY 1 8.0400 -804.00 -1.05 -0.09
  const namedExchRE = /U\d+\s+[\w .]+?\s+\d{4}-\d{2}-\d{2},\s*[\d:]+\s+\d{4}-\d{2}-\d{2}\s+(\w+)\s+(?:BUY|SELL)\s+[-\d]+\s+[\d.]+\s+[-\d,]+\.?\d*\s+([-\d.]+)\s+([-\d.]+)/gm;
  let totalComm = 0;
  let cr;
  while ((cr = namedExchRE.exec(text)) !== null) {
    const comm = parseFloat(cr[2]) || 0;
    const fee  = parseFloat(cr[3]) || 0;
    if (comm < 0) totalComm += comm;
    if (fee  < 0) totalComm += fee;
  }
  logFn(`   Total commissions+fees: $${totalComm.toFixed(2)}`);
  logFn(`${Object.keys(contractData).length} contracts · ${Object.keys(flatPnls).length} closed`);
  const rawLegs = [];
  for (const [contract, pnl] of Object.entries(flatPnls)) {
    const cd = contractData[contract]; if (!cd) continue;
    const parsed = parseContract(contract); if (!parsed) continue;
    const dates = findTradeDates(text, contract);
    rawLegs.push({ key:`${account}_${contract}_${dates.open}_${dates.close}`, account, contract,
      underlying:parsed.underlying, type:parsed.type, strike:parsed.strike, expiry:parsed.expiry,
      tradeDate:dates.open||'?', closeDate:dates.close||'?',
      boughtQty:Math.abs(cd.bought?.qty||0), soldQty:Math.abs(cd.sold?.qty||0),
      boughtAvg:cd.bought?.avgPrice||0, soldAvg:cd.sold?.avgPrice||0,
      grossPnl:pnl, isWin:pnl>0, multiplier:parsed.type==='STOCK'?1:100 });
  }
  for (const [contract, cd] of Object.entries(contractData)) {
    if (flatPnls[contract]!==undefined || !cd.sold) continue;
    const parsed = parseContract(contract); if (!parsed || parsed.type!=='STOCK') continue;
    const pnl=(cd.sold?.proceeds||0)+(cd.bought?.proceeds||0), dates=findTradeDates(text,contract);
    rawLegs.push({ key:`${account}_${contract}_${dates.open}_${dates.close}`, account, contract,
      underlying:parsed.underlying, type:'STOCK', strike:null, expiry:null,
      tradeDate:dates.open||'?', closeDate:dates.close||'?',
      boughtQty:Math.abs(cd.bought?.qty||0), soldQty:Math.abs(cd.sold?.qty||0),
      boughtAvg:cd.bought?.avgPrice||0, soldAvg:cd.sold?.avgPrice||0,
      grossPnl:pnl, isWin:pnl>0, multiplier:1 });
  }
  logFn(`${rawLegs.length} raw legs → detecting spreads…`);
  const trades = detectSpreads(rawLegs);
  logFn(`${trades.filter(t=>t.isSpread).length} spreads + ${trades.filter(t=>!t.isSpread).length} singles`);
  return { account, name, trades, totalComm };
}

// ── Metrics ──────────────────────────────────────────────────────────────────
function calcMetrics(trades, fallbackTotalComm=0) {
  if (!trades?.length) return null;
  // Per-trade commPnl is set on HTM imports — sum only the relevant filtered set.
  // For legacy PDF imports without commPnl, use the passed fallback (all-time total).
  const hasPerTradeComm = trades.some(t => t.commPnl !== undefined && t.commPnl !== 0);
  const totalComm = hasPerTradeComm
    ? trades.reduce((s,t) => s + (t.commPnl || 0), 0)
    : fallbackTotalComm;
  const wins=trades.filter(t=>t.isWin), losses=trades.filter(t=>!t.isWin);
  const grossTotalPnl=trades.reduce((s,t)=>s+t.grossPnl,0);
  const totalPnl=grossTotalPnl+totalComm;          // net after commission for this filtered set only
  const grossProfit=wins.reduce((s,t)=>s+t.grossPnl,0);
  const grossLoss=Math.abs(losses.reduce((s,t)=>s+t.grossPnl,0));
  const winRate=wins.length/trades.length;
  const avgWin=wins.length?grossProfit/wins.length:0;
  const avgLoss=losses.length?grossLoss/losses.length:0;
  const profitFactor=grossLoss>0?grossProfit/grossLoss:grossProfit>0?Infinity:0; // gross edge
  const expectancy=totalPnl/trades.length;          // true net per trade (after commission)
  return { totalPnl, totalComm, grossProfit, grossLoss, winRate, avgWin, avgLoss,
    profitFactor, expectancy, wins:wins.length, losses:losses.length, total:trades.length };
}

const fmtD=(n)=>n==null?'—':(n>=0?'+$':'-$')+Math.abs(n).toFixed(2);
const fmtPf=(n)=>!isFinite(n)?'∞':n.toFixed(2);
const pct=(n)=>(n*100).toFixed(1)+'%';
function isoWeek(ds){const d=new Date(ds+'T00:00:00Z'),day=d.getUTCDay()||7;d.setUTCDate(d.getUTCDate()+4-day);const yr=d.getUTCFullYear();return `${yr}-W${String(Math.ceil((((d-new Date(Date.UTC(yr,0,1)))/86400000)+1)/7)).padStart(2,'0')}`;}

const C={up:'#3fb950',upA:'rgba(63,185,80,0.12)',dn:'#f85149',dnA:'rgba(248,81,73,0.12)',ac:'#388bfd',acA:'rgba(56,139,253,0.12)',wa:'#d29922',pu:'#bc8cff',bd:'#1e2d3d',mu:'#768390'};
const tdSt={padding:'7px 10px',fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:'var(--text)',whiteSpace:'nowrap'};
const thSt={padding:'8px 10px',fontFamily:"'IBM Plex Mono',monospace",fontSize:9,textTransform:'uppercase',letterSpacing:'.08em',color:'var(--muted)',borderBottom:'1px solid var(--border)',background:'var(--bg-elev)',whiteSpace:'nowrap'};
const inSt={background:'var(--bg-elev)',border:'1px solid var(--border-strong)',color:'var(--text)',borderRadius:5,padding:'5px 10px',fontFamily:"'IBM Plex Mono',monospace",fontSize:11,outline:'none'};
const btnSt={padding:'6px 14px',borderRadius:5,fontFamily:"'IBM Plex Mono',monospace",fontSize:11,fontWeight:600,cursor:'pointer',background:'var(--accent)',color:'#fff',border:'none'};

function StatCard({label,value,sub,color}){
  return(<div style={{background:'var(--bg-elev)',border:'1px solid var(--border)',borderRadius:8,padding:'12px 14px'}}>
    <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:9,textTransform:'uppercase',letterSpacing:'.1em',color:'var(--muted)',marginBottom:5}}>{label}</div>
    <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:17,fontWeight:700,color:color||'var(--text)'}}>{value}</div>
    {sub&&<div style={{fontSize:10,color:'var(--muted)',marginTop:2}}>{sub}</div>}
  </div>);
}

function StatsRow({m, totalComm=0}){if(!m)return null;return(
  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(128px,1fr))',gap:10,marginBottom:14}}>
    <StatCard label="Net P&L" value={fmtD(m.totalPnl)} color={m.totalPnl>=0?C.up:C.dn} sub={`${m.total} closed trades · after commissions`}/>
    <StatCard label="Commissions" value={fmtD(totalComm||0)} color={'var(--down)'} sub="Filtered set · incl. fees"/>
    <StatCard label="Win Rate" value={pct(m.winRate)} color={C.ac} sub={`${m.wins}W · ${m.losses}L`}/>
    <StatCard label="Profit Factor" value={fmtPf(m.profitFactor)} color={m.profitFactor>=1?C.up:C.dn} sub="Gross (pre-commission)"/>
    <StatCard label="Expectancy" value={fmtD(m.expectancy)} color={m.expectancy>=0?C.up:C.dn} sub="Net per trade · after fees"/>
    <StatCard label="Avg Win" value={`+$${m.avgWin.toFixed(2)}`} color={C.up} sub={`Gross $${m.grossProfit.toFixed(0)}`}/>
    <StatCard label="Avg Loss" value={`-$${m.avgLoss.toFixed(2)}`} color={C.dn} sub={`Gross $${m.grossLoss.toFixed(0)}`}/>
  </div>);}

function TypeBadge({type,spreadType}){
  const map={SPREAD:[C.ac,C.acA],CALL:[C.up,C.upA],PUT:[C.pu,C.pu+'20'],STOCK:[C.wa,'rgba(210,153,34,0.12)']};
  const [col,bg]=map[type]||[C.mu,''];
  return(<span style={{display:'inline-block',padding:'2px 5px',borderRadius:3,fontSize:9,fontWeight:700,letterSpacing:'.05em',textTransform:'uppercase',color:col,background:bg,marginRight:2}}>
    {type==='SPREAD'&&spreadType?spreadType:type}
  </span>);}

function PnlTable({rows}){return(
  <div style={{border:'1px solid var(--border)',borderRadius:8,overflow:'hidden'}}>
    <table style={{width:'100%',borderCollapse:'collapse'}}>
      <thead><tr>{['Period','Trades','Wins','P&L'].map((s,i)=><th key={i} style={thSt}>{s}</th>)}</tr></thead>
      <tbody>{rows.map(([k,ts])=>{const pnl=ts.reduce((s,t)=>s+t.grossPnl,0),wins=ts.filter(t=>t.isWin).length;return(
        <tr key={k} style={{borderTop:'1px solid var(--border)'}}>
          <td style={tdSt}>{k}</td><td style={{...tdSt,textAlign:'right'}}>{ts.length}</td>
          <td style={{...tdSt,textAlign:'right'}}>{wins}</td>
          <td style={{...tdSt,textAlign:'right',color:pnl>=0?C.up:C.dn}}>{fmtD(pnl)}</td>
        </tr>);})}</tbody>
    </table>
  </div>);}

// ── Import Tab ───────────────────────────────────────────────────────────────
// ── IBKR Flex XML Parser ──────────────────────────────────────────────────────
// Parses the Flex Query "Trades" section XML from IBKR's Flex Web Service.
// Select the "Trades" section in your Flex Query with these fields:
//   Symbol, UnderlyingSymbol, PutCall, Strike, Expiry, BuySell, Quantity,
//   TradePrice, Proceeds, IBCommission, DateTime, OpenCloseIndicator,
//   FifoPnlRealized, AssetCategory
function parseFlexXML(xmlText, logFn) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, 'application/xml');
  if (doc.querySelector('parsererror')) { logFn('❌ XML parse error — is this a valid Flex Query response?'); return null; }

  const stmt    = doc.querySelector('FlexStatement');
  const account = stmt?.getAttribute('accountId') ?? '';
  const fromDt  = stmt?.getAttribute('fromDate') ?? '';
  const toDt    = stmt?.getAttribute('toDate') ?? '';
  logFn(`   Account: ${account} · ${fromDt} → ${toDt}`);

  const fmtDate = (s) => {
    if (!s) return '?';
    const d = s.replace(/;.*$/, '').trim();          // strip time part "2026-05-18;09:30"
    if (/^\d{8}$/.test(d)) return `${d.slice(0,4)}-${d.slice(4,6)}-${d.slice(6,8)}`;
    return d;
  };

  // Group Trade elements by contract (symbol+putCall+strike+expiry)
  // Sum fifoPnlRealized and ibCommission across all closing executions per contract.
  // Opening trades have fifoPnlRealized=0 so summing all trades is safe.
  // Track openQty (signed: +buy/-sell on opening rows) to derive openedShort for spread detection.
  const byContract = {};   // key → { under, putCall, strike, expiry, pnl, comm, openDts, closeDts, openQty }

  doc.querySelectorAll('Trade').forEach(tr => {
    const cat     = tr.getAttribute('assetClass') || tr.getAttribute('assetCategory') || '';
    if (cat && cat !== 'OPT') return;               // options only (assetClass=OPT)
    const putCall = tr.getAttribute('putCall');
    if (!putCall || putCall === '?') return;

    const under   = tr.getAttribute('underlyingSymbol') ?? '';
    const strike  = parseFloat(tr.getAttribute('strike') ?? '0');
    const expiry  = fmtDate(tr.getAttribute('expiry') ?? '');
    const dateStr = fmtDate(tr.getAttribute('dateTime') || tr.getAttribute('tradeDate') || tr.getAttribute('reportDate') || '');
    const pnl     = parseFloat(tr.getAttribute('realizedPnl') || tr.getAttribute('fifoPnlRealized') || '0');
    const comm    = parseFloat(tr.getAttribute('ibCommission') ?? '0') +
                    parseFloat(tr.getAttribute('taxes') ?? '0');
    const oci     = tr.getAttribute('openCloseIndicator') ?? '';  // "O", "C", "C;O", etc.
    const buySell = (tr.getAttribute('buySell') ?? '').toUpperCase();
    const qty     = Math.abs(parseFloat(tr.getAttribute('quantity') ?? '0'));

    if (!under || !strike || !expiry) return;

    // Build stable contract key
    const expYY = expiry.slice(2,4), expMM = expiry.slice(5,7), expDD = expiry.slice(8,10);
    const strikeInt = Math.round(strike * 1000);
    const occ = `${under} ${expYY}${expMM}${expDD}${putCall}${String(strikeInt).padStart(8,'0')}`;
    const k   = `${account}|${occ}`;

    if (!byContract[k]) byContract[k] = { under, putCall, strike, expiry, occ, pnl:0, comm:0, openDts:[], closeDts:[], openQty:0 };
    const rec = byContract[k];
    rec.pnl  += pnl;
    rec.comm += comm;    // commission is present on both opening and closing trades

    const isClose = oci.includes('C');
    const isOpen  = oci.includes('O');
    if (isOpen  && dateStr !== '?') rec.openDts.push(dateStr);
    if (isClose && dateStr !== '?') rec.closeDts.push(dateStr);
    else if (dateStr !== '?') rec.closeDts.push(dateStr);   // fallback

    // Accumulate signed opening quantity: +qty = bought to open, -qty = sold to open
    // C;O rows (same-day flip) count the O portion for direction
    if (isOpen) rec.openQty += (buySell === 'BUY' ? qty : -qty);
  });

  // Probe: log the actual element names present in the XML to diagnose tag mismatch
  const allTags = {};
  doc.querySelectorAll('*').forEach(el => { allTags[el.tagName] = (allTags[el.tagName]||0)+1; });
  const tagSummary = Object.entries(allTags).filter(([,n])=>n>0).map(([t,n])=>`${t}(${n})`).join(' ');
  logFn(`   XML elements: ${tagSummary}`);

  // Diagnostic: log what we actually parsed before filtering
  const allContracts = Object.entries(byContract);
  logFn(`   Raw contracts parsed: ${allContracts.length}`);
  if (allContracts.length <= 20) {
    allContracts.forEach(([k, r]) => {
      logFn(`   ${k.split('|')[1] ?? k} pnl=${r.pnl.toFixed(2)} comm=${r.comm.toFixed(2)} openQty=${r.openQty} opens=${r.openDts.length} closes=${r.closeDts.length}`);
    });
  }
  const rawLegs = allContracts
    .filter(([, r]) => r.pnl !== 0 || r.comm !== 0)         // skip flat / still-open legs
    .map(([, r]) => {
      const openDt  = r.openDts.length  ? [...r.openDts].sort()[0]  : '?';
      const closeDt = r.closeDts.length ? [...r.closeDts].sort().pop() : '?';
      // openQty > 0 = bought to open (long leg); < 0 = sold to open (short leg)
      const openedShort = r.openQty < 0 ? true : r.openQty > 0 ? false : null;
      return {
        key:        `${account}_flex_${r.occ}_${openDt}_${closeDt}`,
        account,    contract: r.occ,
        underlying: r.under,
        type:       r.putCall === 'C' ? 'CALL' : 'PUT',
        strike:     r.strike, expiry: r.expiry,
        tradeDate:  openDt,   closeDate: closeDt,
        boughtQty:  r.openQty > 0 ? Math.abs(r.openQty) : 0,
        soldQty:    r.openQty < 0 ? Math.abs(r.openQty) : 0,
        grossPnl:   r.pnl, commPnl: r.comm,
        isWin:      r.pnl > 0, multiplier: 100,
        openedShort, source: 'flex',
      };
    });

  if (!rawLegs.length) {
    logFn('⚠ No closed option trades found in the Flex XML.');
    logFn('   Make sure the "Trades" section is selected in your Flex Query.');
    logFn('   Required fields: Asset Class, Symbol, Underlying Symbol, Put/Call, Strike, Expiry, Quantity, Proceeds, IB Commission, Open/Close Indicator, Realized P/L, Date/Time');
    return null;
  }
  logFn(`   ${rawLegs.length} closed option contracts found`);

  const result  = detectSpreads(rawLegs);
  const ttlComm = rawLegs.reduce((s, l) => s + (l.commPnl||0), 0);
  logFn(`   After spread pairing: ${result.length} positions`);
  logFn(`   Total commissions: $${ttlComm.toFixed(2)}`);
  return { account, name: 'IBKR Flex', trades: result, totalComm: ttlComm };
}

// Parses Trade Confirmation Flex Query XML (<TradeConfirm> elements).
// Confirmation reports have no P&L per row — they are raw executions.
// Strategy: group all executions by contract (account|OCC), accumulate
// netCash (signed: positive = cash in = sell, negative = cash out = buy)
// and commission. A fully-closed contract will net close to zero quantity.
// We surface every contract seen as an "execution group" for review.
function parseTradeConfirmXML(xmlText, logFn) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, 'application/xml');
  if (doc.querySelector('parsererror')) {
    logFn('❌ XML parse error — is this a valid Flex Query response?');
    return null;
  }

  const stmt    = doc.querySelector('FlexStatement');
  const account = stmt?.getAttribute('accountId') ?? '';
  const fromDt  = stmt?.getAttribute('fromDate') ?? '';
  const toDt    = stmt?.getAttribute('toDate') ?? '';
  logFn(`   Account: ${account} · ${fromDt} → ${toDt}`);

  const fmtDate = (s) => {
    if (!s) return '?';
    // Handle "20260605;09:30:00" or "2026-06-05;09:30" formats
    const d = s.split(';')[0].trim();
    if (/^\d{8}$/.test(d)) return `${d.slice(0,4)}-${d.slice(4,6)}-${d.slice(6,8)}`;
    return d;
  };

  const confirms = doc.querySelectorAll('TradeConfirm');
  logFn(`   Found ${confirms.length} TradeConfirm rows`);
  if (!confirms.length) {
    logFn('⚠ No TradeConfirm elements found in the XML.');
    logFn('   Make sure the Flex Query type is "Trade Confirmation" and format is XML.');
    return null;
  }

  // Group executions by contract
  const byContract = {}; // key → { under, putCall, strike, expiry, occ, netCash, comm, qty, dates }

  confirms.forEach(tc => {
    const cat = tc.getAttribute('assetClass') || tc.getAttribute('assetCategory') || '';
    if (cat && cat !== 'OPT') return;
    const putCall = tc.getAttribute('putCall');
    if (!putCall || putCall === '?' || putCall === '') return;

    const under   = tc.getAttribute('underlyingSymbol') ?? '';
    const strike  = parseFloat(tc.getAttribute('strike') ?? '0');
    const expiry  = fmtDate(tc.getAttribute('expiry') ?? '');
    // dateTime preferred; fall back to tradeDate
    const dateStr = fmtDate(
      tc.getAttribute('dateTime') || tc.getAttribute('tradeDate') ||
      tc.getAttribute('orderTime') || tc.getAttribute('settleDate') || ''
    );
    const qty     = parseFloat(tc.getAttribute('quantity') ?? '0');
    const buySell = (tc.getAttribute('buySell') ?? '').toUpperCase();
    // netCash is already signed correctly by IBKR (sell = positive, buy = negative)
    const netCash = parseFloat(tc.getAttribute('netCash') ?? tc.getAttribute('proceeds') ?? '0');
    const comm    = parseFloat(tc.getAttribute('commission') ?? tc.getAttribute('ibCommission') ?? '0');
    const price   = parseFloat(tc.getAttribute('price') ?? tc.getAttribute('tradePrice') ?? '0');

    if (!under || !strike || !expiry) return;

    const expYY = expiry.slice(2,4), expMM = expiry.slice(5,7), expDD = expiry.slice(8,10);
    const strikeInt = Math.round(strike * 1000);
    const occ = `${under} ${expYY}${expMM}${expDD}${putCall}${String(strikeInt).padStart(8,'0')}`;
    const k   = `${account}|${occ}`;

    if (!byContract[k]) byContract[k] = {
      under, putCall, strike, expiry, occ,
      netCash: 0, comm: 0, netQty: 0,
      openDts: [], closeDts: [], prices: [],
    };
    const rec = byContract[k];
    rec.netCash += netCash;
    rec.comm    += comm;
    // Signed qty: sells are negative contracts from buyer perspective
    rec.netQty  += (buySell === 'BUY' ? qty : -qty);
    rec.prices.push(price);
    if (dateStr !== '?') {
      // We don't have open/close indicator, so track all dates
      if (buySell === 'BUY') rec.openDts.push(dateStr);
      else rec.closeDts.push(dateStr);
    }
  });

  const legs = Object.entries(byContract).map(([, r]) => {
    const openDt  = r.openDts.length  ? [...r.openDts].sort()[0]    : '?';
    const closeDt = r.closeDts.length ? [...r.closeDts].sort().pop() : openDt;
    const grossPnl = r.netCash; // best approximation without realized P&L field
    const avgPrice = r.prices.length ? r.prices.reduce((a,b)=>a+b,0)/r.prices.length : 0;
    // Infer direction from net signed quantity across all executions:
    //   netQty < 0 = net short (sold more than bought) → opened short
    //   netQty > 0 = net long  (bought more than sold) → opened long
    // For round-trips netQty ≈ 0; fall back to netCash sign (credit = short, debit = long)
    const openedShort = r.netQty < 0 ? true
                      : r.netQty > 0 ? false
                      : r.netCash > 0 ? true   // received premium → sold to open
                      : r.netCash < 0 ? false  // paid premium → bought to open
                      : null;
    return {
      key:          `${account}_conf_${r.occ}_${openDt}_${closeDt}`,
      account,      contract: r.occ,
      underlying:   r.under,
      type:         r.putCall === 'C' ? 'CALL' : 'PUT',
      strike:       r.strike, expiry: r.expiry,
      tradeDate:    openDt,   closeDate: closeDt,
      boughtQty:    r.netQty > 0 ? Math.abs(r.netQty) : 0,
      soldQty:      r.netQty < 0 ? Math.abs(r.netQty) : 0,
      grossPnl,     commPnl: r.comm,
      isWin:        grossPnl > 0,
      multiplier:   100,
      openedShort,
      avgPrice,     netQty: r.netQty,
      source:       'confirmation',
    };
  });

  if (!legs.length) {
    logFn('⚠ No option TradeConfirm rows found after filtering.');
    logFn('   Ensure Asset Class, Symbol, Underlying Symbol, Put/Call, Strike, Expiry are ticked.');
    return null;
  }
  logFn(`   ${legs.length} option contract group${legs.length!==1?'s':''} found`);

  const result   = detectSpreads(legs);
  const ttlComm  = legs.reduce((s,l) => s + (l.commPnl||0), 0);
  logFn(`   After spread pairing: ${result.length} positions`);
  logFn(`   Total commissions: $${ttlComm.toFixed(2)}`);
  logFn('   ℹ P&L shown is net cash flow (no realized P&L in confirmation reports)');
  return { account, name: 'IBKR Flex (Confirmations)', trades: result, totalComm: ttlComm };
}

// ── IBKR Connect widget (shown in Import tab) ──────────────────────────────────
function IBKRConnect({ onImport }) {
  const [status,       setStatus]       = useState(null);  // null | {configured, queryId, confQueryId, savedAt}
  const [token,        setToken]        = useState('');
  const [queryId,      setQueryId]      = useState('');
  const [confQueryId,  setConfQueryId]  = useState('');
  const [days,         setDays]         = useState('14');
  const [editing,      setEditing]      = useState(false);
  const [fetching,     setFetching]     = useState(false);   // activity fetch in progress
  const [fetchingConf, setFetchingConf] = useState(false);   // confirmation fetch in progress
  const [msg,          setMsg]          = useState('');

  useEffect(() => {
    ibkrGetStatus().then(s => { setStatus(s); }).catch(() => {});
  }, []);

  const saveCredentials = async () => {
    if (!token.trim() || !queryId.trim()) { setMsg('Flex Token and Activity Query ID are required.'); return; }
    setMsg('Saving…');
    try {
      await ibkrSaveCredentials(token.trim(), queryId.trim(), confQueryId.trim() || null);
      const s = await ibkrGetStatus();
      setStatus(s); setEditing(false); setMsg('✓ Credentials saved');
      setToken(''); setQueryId(''); setConfQueryId('');
    } catch(e) { setMsg('Error: ' + e.message); }
  };

  const fetchReport = async () => {
    setFetching(true); setMsg('Requesting activity report from IBKR…');
    try {
      const xml = await ibkrFetchReport(Number(days));
      onImport(xml);
      setMsg('✓ Activity report fetched — importing trades…');
    } catch(e) { setMsg('Error: ' + e.message); }
    finally { setFetching(false); }
  };

  const fetchConfReport = async () => {
    setFetchingConf(true); setMsg('Requesting trade confirmations from IBKR…');
    try {
      const xml = await ibkrFetchConfReport(Number(days));
      onImport(xml);
      setMsg('✓ Confirmation report fetched — importing trades…');
    } catch(e) { setMsg('Error: ' + e.message); }
    finally { setFetchingConf(false); }
  };

  const configured    = status?.configured;
  const confConfigured = !!(status?.confQueryId);
  const anyFetching   = fetching || fetchingConf;

  const selectEl = (
    <select value={days} onChange={e => setDays(e.target.value)}
      style={{ padding:'6px 10px', background:'var(--bg)', border:'1px solid var(--border)',
        borderRadius:5, color:'var(--text)', fontSize:11, fontFamily:"'IBM Plex Mono',monospace" }}>
      <option value="7">Last 7 days</option>
      <option value="14">Last 14 days</option>
      <option value="30">Last 30 days</option>
      <option value="60">Last 60 days</option>
      <option value="90">Last 90 days</option>
      <option value="365">Last 1 year</option>
    </select>
  );

  return (
    <div style={{ border:'1px solid var(--border)', borderRadius:8, padding:'14px 16px',
      marginBottom:16, background:'var(--bg)' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontWeight:800, fontSize:13, color:'var(--text)' }}>IBKR Flex Connect</span>
          <span style={{ fontSize:10, padding:'2px 7px', borderRadius:3, fontWeight:700, letterSpacing:'.06em',
            background: configured ? '#22c55e20' : 'var(--border)',
            color: configured ? '#22c55e' : 'var(--faint)',
            border: `1px solid ${configured ? '#22c55e50' : 'var(--border)'}` }}>
            {configured ? '● CONNECTED' : '○ NOT SET UP'}
          </span>
        </div>
        {configured && !editing && (
          <button onClick={() => { setEditing(true); }}
            style={{ fontSize:10, padding:'3px 9px', border:'1px solid var(--border)', borderRadius:4,
              background:'transparent', color:'var(--muted)', cursor:'pointer' }}>
            Update credentials
          </button>
        )}
      </div>

      {configured && !editing ? (
        /* ── Connected state ── */
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {/* Query IDs summary */}
          <div style={{ fontSize:11, color:'var(--muted)', lineHeight:1.8 }}>
            <div>
              Activity query <span style={{ fontFamily:'monospace', color:'var(--text)' }}>{status.queryId}</span>
              {status.savedAt && <span style={{ color:'var(--faint)', marginLeft:8 }}>
                · saved {new Date(status.savedAt).toLocaleDateString()}
              </span>}
            </div>
            <div>
              Confirmation query{' '}
              {confConfigured
                ? <span style={{ fontFamily:'monospace', color:'var(--text)' }}>{status.confQueryId}</span>
                : <span style={{ color:'var(--faint)', fontStyle:'italic' }}>not set — add via Update credentials</span>
              }
            </div>
          </div>

          {/* Fetch controls */}
          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
            {selectEl}
            {/* Activity report */}
            <button onClick={fetchReport} disabled={anyFetching}
              style={{ padding:'7px 18px', borderRadius:5, border:'none', fontWeight:700, fontSize:12,
                background: fetching ? 'var(--border)' : 'var(--accent)',
                color: fetching ? 'var(--faint)' : 'var(--bg)', cursor: anyFetching ? 'default' : 'pointer' }}>
              {fetching ? '⟳ Fetching…' : '⬇ Fetch from IBKR'}
            </button>
            {/* Confirmation report — only shown when confQueryId is set */}
            {confConfigured && (
              <button onClick={fetchConfReport} disabled={anyFetching}
                title="Fetch trade confirmation report using your confirmation Flex Query"
                style={{ padding:'7px 14px', borderRadius:5, fontWeight:700, fontSize:12,
                  border:'1px solid var(--accent)', cursor: anyFetching ? 'default' : 'pointer',
                  background: fetchingConf ? 'var(--border)' : 'transparent',
                  color: fetchingConf ? 'var(--faint)' : 'var(--accent)',
                  opacity: anyFetching && !fetchingConf ? 0.45 : 1 }}>
                {fetchingConf ? '⟳ Fetching…' : '⬇ Fetch Confirmations'}
              </button>
            )}
          </div>
          {msg && <div style={{ fontSize:11, color: msg.startsWith('✓')?'var(--up)':msg.startsWith('Error')?'var(--down)':'var(--muted)' }}>{msg}</div>}
        </div>
      ) : (
        /* ── Setup / Edit credentials ── */
        <div>
          <div style={{ fontSize:11, color:'var(--muted)', lineHeight:1.7, marginBottom:12 }}>
            Set up a <strong style={{color:'var(--text)'}}>Flex Query</strong> in IBKR once, then fetch trades instantly:
            <ol style={{ margin:'6px 0 0 16px', padding:0 }}>
              <li>IBKR → Reports → Flex Queries → Manage Flex Queries → <strong>Create</strong></li>
              <li>Name it anything · type: <strong>Activity</strong> · sections: ✓ <strong>Trades</strong></li>
              <li>Trades fields to tick: <strong>Asset Class, Symbol, Underlying Symbol, Put/Call, Strike, Expiry, Date/Time, Quantity, Trade Price, Proceeds, IB Commission, Open/Close Indicator, Realized P/L</strong></li>
              <li>Date period: <em>Last N Calendar Days</em> (you choose N when fetching)</li>
              <li>Save → note the <strong>Query ID</strong> shown next to the query name</li>
              <li>Flex Web Service page → copy your <strong>Token</strong></li>
            </ol>
          </div>

          {/* ── Main credentials grid ── */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, maxWidth:560 }}>
            <div>
              <div style={{ fontSize:9, color:'var(--faint)', marginBottom:3, textTransform:'uppercase', letterSpacing:'.08em' }}>Flex Token</div>
              <input type="password" value={token} onChange={e=>setToken(e.target.value)}
                placeholder="Paste token…" autoComplete="off"
                style={{ width:'100%', padding:'7px 10px', background:'var(--bg)', border:'1px solid var(--border)',
                  borderRadius:5, color:'var(--text)', fontSize:11, fontFamily:"'IBM Plex Mono',monospace", boxSizing:'border-box' }}/>
            </div>
            <div>
              <div style={{ fontSize:9, color:'var(--faint)', marginBottom:3, textTransform:'uppercase', letterSpacing:'.08em' }}>Activity Query ID</div>
              <input type="text" value={queryId} onChange={e=>setQueryId(e.target.value)}
                placeholder="e.g. 123456" autoComplete="off"
                style={{ width:'100%', padding:'7px 10px', background:'var(--bg)', border:'1px solid var(--border)',
                  borderRadius:5, color:'var(--text)', fontSize:11, fontFamily:"'IBM Plex Mono',monospace", boxSizing:'border-box' }}/>
            </div>
          </div>

          {/* ── Confirmation Query ID (optional) ── */}
          <div style={{ marginTop:12, paddingTop:12, borderTop:'1px solid var(--border)' }}>
            <div style={{ fontSize:9, color:'var(--faint)', marginBottom:3, textTransform:'uppercase', letterSpacing:'.08em' }}>
              Confirmation Query ID <span style={{ color:'var(--muted)', fontStyle:'italic', textTransform:'none', letterSpacing:0 }}>(optional)</span>
            </div>
            <div style={{ fontSize:10, color:'var(--muted)', marginBottom:6, lineHeight:1.5 }}>
              Create a second Flex Query of type <strong style={{color:'var(--text)'}}>Trade Confirmation</strong> in IBKR.
              Paste its Query ID here to enable a separate "Fetch Confirmations" button that pulls confirmation data using the same token.
            </div>
            <input type="text" value={confQueryId} onChange={e=>setConfQueryId(e.target.value)}
              placeholder="e.g. 789012 — leave blank to skip"
              autoComplete="off"
              style={{ width:'100%', maxWidth:280, padding:'7px 10px', background:'var(--bg)', border:'1px solid var(--border)',
                borderRadius:5, color:'var(--text)', fontSize:11, fontFamily:"'IBM Plex Mono',monospace", boxSizing:'border-box' }}/>
          </div>

          <div style={{ display:'flex', gap:8, marginTop:12 }}>
            <button onClick={saveCredentials}
              style={{ padding:'7px 18px', borderRadius:5, border:'none', fontWeight:700, fontSize:12,
                background:'var(--accent)', color:'var(--bg)', cursor:'pointer' }}>
              Save credentials
            </button>
            {editing && <button onClick={() => { setEditing(false); setMsg(''); setToken(''); setQueryId(''); setConfQueryId(''); }}
              style={{ padding:'7px 12px', borderRadius:5, border:'1px solid var(--border)',
                background:'transparent', color:'var(--muted)', cursor:'pointer', fontSize:12 }}>
              Cancel
            </button>}
          </div>
          {msg && <div style={{ fontSize:11, marginTop:8, color: msg.startsWith('✓')?'var(--up)':msg.startsWith('Error')?'var(--down)':'var(--muted)' }}>{msg}</div>}
          <div style={{ fontSize:10, color:'var(--faint)', marginTop:10, lineHeight:1.5 }}>
            Credentials are stored in your private KV store — never logged or shared. Only your account can access them.
          </div>
        </div>
      )}
    </div>
  );
}

// ── Journal Settings Tab ──────────────────────────────────────────────────────
// Top section: Import (IBKR Flex + HTM file drop)
// Below: Overview start date filter and other journal settings
function JournalSettingsTab({ importLog, logVisible, onFile, onDrop, onIBKRXml, startDate, onStartDate, trades, records, onClearAll }) {
  const settingSection = (title) => (
    <div style={{ fontSize:11, fontWeight:800, letterSpacing:'.08em', textTransform:'uppercase',
      color:'var(--muted)', marginBottom:12, paddingBottom:6,
      borderBottom:'1px solid var(--border)' }}>{title}</div>
  );

  return (
    <div style={{ maxWidth:680 }}>

      {/* ── Import ─────────────────────────────────────────────────────── */}
      <div style={{ marginBottom:28 }}>
        {settingSection('⬆ Import Trades')}
        <IBKRConnect onImport={onIBKRXml}/>

        {/* HTM / PDF drop zone */}
        <div style={{ marginTop:14 }}>
          <div style={{ fontSize:11, color:'var(--muted)', marginBottom:8, lineHeight:1.6 }}>
            Or import manually — drag a <span style={{color:'var(--accent)',fontWeight:700}}>.xml</span>, <span style={{color:'var(--accent)'}}>.htm</span>, or <span style={{color:'var(--muted)'}}>.pdf</span> file downloaded from IBKR.
          </div>
          <div onDragOver={e=>e.preventDefault()} onDrop={onDrop}
            onClick={()=>document.getElementById('jrnl-file-input').click()}
            style={{border:'2px dashed var(--border-strong)',borderRadius:8,padding:'28px 20px',
              textAlign:'center',cursor:'pointer',transition:'all .2s'}}
            onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--accent)';e.currentTarget.style.background='rgba(56,139,253,0.05)';}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor='';e.currentTarget.style.background='';}}>
            <div style={{fontSize:24,marginBottom:6}}>📄</div>
            <div style={{fontWeight:600,fontSize:13,marginBottom:4}}>Drop HTM / PDF here or click to browse</div>
            <div style={{color:'var(--faint)',fontSize:11}}>Spread legs are paired automatically · Duplicates replaced</div>
          </div>
          <input id="jrnl-file-input" type="file" accept=".xml,.htm,.html,.pdf" style={{display:'none'}} onChange={e=>onFile(e.target.files[0])}/>
          {logVisible&&importLog.length>0&&(
            <div style={{marginTop:10,background:'var(--bg-elev)',border:'1px solid var(--border)',borderRadius:7,
              padding:'10px 12px',fontFamily:"'IBM Plex Mono',monospace",fontSize:10,maxHeight:160,overflowY:'auto'}}>
              {importLog.map((l,i)=><div key={i} style={{color:l.cls==='ok'?C.up:l.cls==='err'?C.dn:C.mu,padding:'1px 0'}}>{l.msg}</div>)}
            </div>
          )}
        </div>
      </div>

      {/* ── Overview start date ─────────────────────────────────────────── */}
      <div style={{ marginBottom:28 }}>
        {settingSection('📊 Overview Date Filter')}
        <div style={{ fontSize:11, color:'var(--muted)', marginBottom:10, lineHeight:1.65 }}>
          Set a start date to filter the <strong>Overview</strong> and <strong>Calendar</strong> tabs —
          only trades closed on or after this date will be counted. Useful for tracking
          a specific period's performance without deleting older trades.
          Performance and Trades tabs are not affected.
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          <div>
            <div style={{ fontSize:9, color:'var(--faint)', marginBottom:3, textTransform:'uppercase', letterSpacing:'.08em' }}>
              Show data from
            </div>
            <input type="date" value={startDate} onChange={e=>onStartDate(e.target.value)}
              style={{ padding:'7px 10px', background:'var(--bg)', border:'1px solid var(--border)',
                borderRadius:5, color:'var(--text)', fontSize:12, fontFamily:"'IBM Plex Mono',monospace",
                cursor:'pointer' }}/>
          </div>
          {startDate && (
            <button onClick={()=>onStartDate('')}
              style={{ alignSelf:'flex-end', padding:'7px 12px', borderRadius:5,
                border:'1px solid var(--border)', background:'transparent',
                color:'var(--muted)', cursor:'pointer', fontSize:11 }}>
              Clear (show all)
            </button>
          )}
        </div>
        {startDate && (
          <div style={{ marginTop:8, fontSize:11, color:'var(--muted)' }}>
            {(() => {
              const filtered = trades.filter(t=>(t.closeDate||t.tradeDate||'')>=startDate);
              return `${filtered.length} of ${trades.length} trades in this range.`;
            })()}
          </div>
        )}
      </div>

      {/* ── Danger zone ────────────────────────────────────────────────── */}
      <div>
        {settingSection('⚠ Danger Zone')}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'12px 14px', borderRadius:7, border:'1px solid var(--down)', background:'var(--down)08' }}>
          <div>
            <div style={{ fontSize:12, fontWeight:700, color:'var(--down)', marginBottom:2 }}>Clear all trade data</div>
            <div style={{ fontSize:11, color:'var(--faint)' }}>
              Permanently removes {trades.length} trades and {records.length} import records.
            </div>
          </div>
          <button onClick={onClearAll}
            style={{ padding:'7px 16px', borderRadius:5, border:'1px solid var(--down)',
              background:'transparent', color:'var(--down)', cursor:'pointer', fontWeight:700, fontSize:12 }}>
            Clear all
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Import Tab ─────────────────────────────────────────────────────────────────
function ImportTab({log,logVisible,onFile,onDrop,onIBKRXml}){return(
  <div>
    {/* IBKR Live Connect */}
    <IBKRConnect onImport={onIBKRXml}/>

    {/* HTM manual import */}
    <div style={{background:'rgba(56,139,253,0.08)',border:'1px solid rgba(56,139,253,0.3)',borderRadius:8,padding:'10px 16px',marginBottom:14,display:'flex',gap:12,alignItems:'flex-start'}}>
      <span style={{fontSize:18,flexShrink:0}}>📄</span>
      <div>
        <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,fontWeight:700,color:'var(--accent)',marginBottom:4}}>Use HTM for best results</div>
        <div style={{fontSize:11,color:'var(--muted)',lineHeight:1.65}}>
          In IBKR: <span style={{fontFamily:'monospace',color:'var(--text)'}}>Reports → Trade Confirmation → Download → HTML</span>.
          HTM gives exact commissions on every import (including multi-day), zero date ambiguity, and instant parsing — no PDF library needed.
          PDF is still supported as a fallback.
        </div>
      </div>
    </div>

    <div onDragOver={e=>e.preventDefault()} onDrop={onDrop}
      onClick={()=>document.getElementById('jrnl-file-input').click()}
      style={{border:'2px dashed var(--border-strong)',borderRadius:10,padding:'44px 24px',textAlign:'center',cursor:'pointer',maxWidth:560,margin:'0 auto',transition:'all .2s'}}
      onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--accent)';e.currentTarget.style.background='rgba(56,139,253,0.05)';}}
      onMouseLeave={e=>{e.currentTarget.style.borderColor='';e.currentTarget.style.background='';}}>
      <div style={{fontSize:32,marginBottom:10}}>📄</div>
      <div style={{fontWeight:600,marginBottom:6,fontSize:14}}>Drop IBKR Trade Confirmation</div>
      <div style={{color:'var(--muted)',fontSize:12,lineHeight:1.6}}>
        <span style={{color:'var(--accent)',fontWeight:700}}>.xml</span> or <span style={{color:'var(--accent)'}}>.htm</span> (recommended) or <span style={{color:'var(--muted)'}}>.pdf</span><br/>
        Spread legs grouped automatically · Duplicates skipped<br/>Synced to your server account
      </div>
    </div>
    <input id="jrnl-file-input" type="file" accept=".xml,.htm,.html,.pdf" style={{display:'none'}} onChange={e=>onFile(e.target.files[0])}/>
    {logVisible&&log.length>0&&(
      <div style={{maxWidth:560,margin:'14px auto 0',background:'var(--bg-elev)',border:'1px solid var(--border)',borderRadius:8,padding:'12px 14px',fontFamily:"'IBM Plex Mono',monospace",fontSize:11,maxHeight:200,overflowY:'auto'}}>
        {log.map((l,i)=><div key={i} style={{color:l.cls==='ok'?C.up:l.cls==='err'?C.dn:l.cls==='warn'?C.wa:C.mu,padding:'1px 0'}}>{l.msg}</div>)}
      </div>)}
  </div>);}

// ── Overview Tab ─────────────────────────────────────────────────────────────
function OverviewTab({trades, records=[], startDate=''}) {
  // Sum commission from all records that added new trades
  const totalComm = records.filter(r => (r.added||0) > 0).reduce((s,r) => s + (r.totalComm||0), 0);
  // Use canvas refs instead of getElementById to avoid race with async Chart.js load
  const eqRef=useRef(null), dyRef=useRef(null), tkRef=useRef(null), tyRef=useRef(null);
  const chartRefs=useRef({});
  // Stable fingerprint — only redraw when trade count or total P&L changes
  const tradeKey = trades.length + ':' + trades.reduce((s,t)=>s+t.grossPnl,0).toFixed(0);

  useEffect(()=>{
    if(!trades.length) return;
    let cancelled=false;
    ensureChartJs().then(async Chart=>{
      if(cancelled) return;
      // Defer to next frame to ensure canvas elements are committed to DOM
      await new Promise(r => requestAnimationFrame(r));
      if(cancelled) return;
      if(!eqRef.current||!dyRef.current||!tkRef.current||!tyRef.current) return;
      Chart.defaults.color=C.mu;Chart.defaults.borderColor=C.bd;
      Chart.defaults.font.family="'IBM Plex Mono'";Chart.defaults.font.size=9;
      // Destroy previous instances stored in refs (safe — we hold the instance)
      Object.values(chartRefs.current).forEach(c=>c?.destroy());
      chartRefs.current={};
      const mkOpts=(cb)=>({responsive:true,maintainAspectRatio:false,plugins:{legend:{display:false}},scales:{x:{grid:{color:C.bd},ticks:{color:C.mu,maxTicksLimit:8}},y:{grid:{color:C.bd},ticks:{color:C.mu,callback:cb}}}});
      const sorted=[...trades].sort((a,b)=>a.closeDate.localeCompare(b.closeDate));
      let run=0;
      const eqV=sorted.map(t=>{run+=t.grossPnl;return run;}),eqL=sorted.map(t=>t.closeDate);
      chartRefs.current.eq=new Chart(eqRef.current,{type:'line',data:{labels:eqL,datasets:[{data:eqV,borderWidth:2,fill:{target:'origin',above:C.up+'18',below:C.dn+'18'},pointRadius:0,tension:0.3,segment:{borderColor:ctx=>ctx.p0.parsed.y<0?C.dn:C.up}}]},options:mkOpts(v=>'$'+v.toFixed(0))});
      const byD={};for(const t of trades)byD[t.closeDate]=(byD[t.closeDate]||0)+t.grossPnl;
      const dK=Object.keys(byD).sort(),dV=dK.map(k=>byD[k]);
      chartRefs.current.dy=new Chart(dyRef.current,{type:'bar',data:{labels:dK,datasets:[{data:dV,backgroundColor:dV.map(v=>v>=0?C.up+'88':C.dn+'88'),borderColor:dV.map(v=>v>=0?C.up:C.dn),borderWidth:1}]},options:mkOpts(v=>'$'+v.toFixed(0))});
      const byT={};for(const t of trades)byT[t.underlying]=(byT[t.underlying]||0)+t.grossPnl;
      // Top 3 winners + top 3 losers by gross P&L
      const allTk=Object.entries(byT).sort((a,b)=>b[1]-a[1]);
      const winners=allTk.filter(([,v])=>v>=0).slice(0,3);
      const losers=allTk.filter(([,v])=>v<0).slice(-3).reverse();
      const topTk=[...winners,...losers];
      const tK=topTk.map(([k])=>k),tV=topTk.map(([,v])=>v);
      chartRefs.current.tk=new Chart(tkRef.current,{type:'bar',data:{labels:tK,datasets:[{data:tV,backgroundColor:tV.map(v=>v>=0?C.up+'88':C.dn+'88'),borderColor:tV.map(v=>v>=0?C.up:C.dn),borderWidth:1}]},options:mkOpts(v=>'$'+v.toFixed(0))});
      const ty={};for(const t of trades)ty[t.type]=(ty[t.type]||0)+1;
      const tyK=Object.keys(ty),tyV=tyK.map(k=>ty[k]),tyC={SPREAD:C.ac,CALL:C.up,PUT:C.pu,STOCK:C.wa};
      chartRefs.current.ty=new Chart(tyRef.current,{type:'doughnut',data:{labels:tyK,datasets:[{data:tyV,backgroundColor:tyK.map(k=>(tyC[k]||C.mu)+'88'),borderColor:tyK.map(k=>tyC[k]||C.mu),borderWidth:1}]},options:{responsive:true,maintainAspectRatio:false,plugins:{legend:{position:'right',labels:{padding:8,boxWidth:8,color:C.mu}}}}});
    });
    return()=>{ cancelled=true; Object.values(chartRefs.current).forEach(c=>c?.destroy()); chartRefs.current={}; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[tradeKey]);
  if(!trades.length)return<div className="empty">Import a PDF to see analytics</div>;
  const m=calcMetrics(trades, totalComm); // totalComm fallback for PDF imports
  const ccStyle={background:'var(--bg-elev)',border:'1px solid var(--border)',borderRadius:8,padding:14};
  const ccHead={fontFamily:"'IBM Plex Mono',monospace",fontSize:9,textTransform:'uppercase',letterSpacing:'.1em',color:'var(--muted)',marginBottom:10};
  return(<div>
    {startDate&&(
      <div style={{marginBottom:10,padding:'6px 12px',borderRadius:6,border:'1px solid var(--accent)',
        background:'var(--accent)0c',fontSize:11,color:'var(--muted)',display:'flex',alignItems:'center',gap:8}}>
        <span style={{color:'var(--accent)',fontWeight:700}}>⊳</span>
        Showing data from <strong style={{color:'var(--text)',fontFamily:'monospace'}}>{startDate}</strong> onwards
        · {trades.length} trades in range
        <span style={{color:'var(--faint)',fontSize:10,marginLeft:4}}>(change in Settings)</span>
      </div>
    )}
    <StatsRow m={m} totalComm={m?.totalComm ?? totalComm}/>
    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
      <div style={{gridColumn:'1/-1',background:'var(--bg-elev)',border:'1px solid var(--border)',borderRadius:8,padding:14}}>
        <div style={ccHead}>Equity Curve</div>
        <div style={{position:'relative',height:155}}><canvas ref={eqRef}/></div>
      </div>
      <div style={ccStyle}><div style={ccHead}>Daily P&L</div><div style={{position:'relative',height:155}}><canvas ref={dyRef}/></div></div>
      <div style={ccStyle}><div style={ccHead}>P&L by Symbol</div><div style={{position:'relative',height:155}}><canvas ref={tkRef}/></div></div>
      <div style={ccStyle}><div style={ccHead}>Trade Types</div><div style={{position:'relative',height:155}}><canvas ref={tyRef}/></div></div>
    </div>
  </div>);}

// ── Performance Tab ──────────────────────────────────────────────────────────
function PerformanceTab({trades, records=[]}){
  const totalComm = records.filter(r => (r.added||0) > 0).reduce((s,r) => s + (r.totalComm||0), 0);
  const allDates=trades.map(t=>t.closeDate).filter(Boolean).sort();
  const minDate=allDates[0]||''; const maxDate=allDates[allDates.length-1]||'';
  const [from,setFrom]=useState(()=>minDate);
  const [to,setTo]=useState(()=>maxDate);
  const [ticker,setTicker]=useState('');
  const [dateField,setDateField]=useState('close');
  const [filterLoaded, setFilterLoaded]=useState(false);
  const chartRef=useRef(null);const perfCanvasRef=useRef(null);
  const tickers=[...new Set(trades.map(t=>t.underlying))].sort();
  const prevMin=useRef(minDate);
  if(prevMin.current!==minDate){prevMin.current=minDate;setFrom(minDate);setTo(maxDate);}

  // Load saved filter from KV on mount (once trades are available)
  useEffect(()=>{
    if(!trades.length||filterLoaded) return;
    journalGetFilter().then(f=>{
      if(f?.from) setFrom(f.from);
      if(f?.to)   setTo(f.to);
      if(f?.ticker && trades.some(t=>t.underlying===f.ticker)) setTicker(f.ticker);
      if(f?.dateField) setDateField(f.dateField);
      setFilterLoaded(true);
    }).catch(()=>setFilterLoaded(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[trades.length]);

  const getFiltered=(f=from,t=to,tk=ticker,df=dateField)=>{
    let ts=trades;
    const field=df==='open'?'tradeDate':'closeDate';
    if(f) ts=ts.filter(x=>(x[field]||'')>=f);
    if(t) ts=ts.filter(x=>(x[field]||'')<=t);
    if(tk) ts=ts.filter(x=>x.underlying===tk);
    return ts;
  };
  const applied=getFiltered();

  const drawChart=useCallback((ts)=>{
    ensureChartJs().then(Chart=>{
      if(chartRef.current){chartRef.current.destroy();chartRef.current=null;}
      if(!ts.length||!perfCanvasRef.current)return;
      const sorted=[...ts].sort((a,b)=>a.closeDate.localeCompare(b.closeDate));
      let run=0;
      const vals=sorted.map(x=>{run+=x.grossPnl+(x.commPnl||0);return run;});
      const labels=sorted.map(x=>x.closeDate);
      // Segment coloring: green above zero, red below zero
      chartRef.current=new Chart(perfCanvasRef.current,{
        type:'line',
        data:{labels,datasets:[{
          data:vals,
          borderWidth:2,
          fill:{target:'origin', above:C.up+'18', below:C.dn+'18'},
          pointRadius:2,tension:0.3,
          segment:{
            borderColor: ctx => ctx.p0.parsed.y < 0 ? C.dn : C.up,
            backgroundColor: ctx => ctx.p0.parsed.y < 0 ? C.dn+'18' : C.up+'18',
          },
        }]},
        options:{
          responsive:true,maintainAspectRatio:false,
          plugins:{legend:{display:false}},
          scales:{
            x:{grid:{color:C.bd},ticks:{color:C.mu,maxTicksLimit:8}},
            y:{grid:{color:C.bd},ticks:{color:C.mu,callback:v=>'$'+v.toFixed(0)},
               // Draw zero line explicitly in a neutral colour
               afterDataLimits: scale => { scale.min = Math.min(scale.min, -1); }},
          },
        },
      });
    });
  },[]);

  const filteredKey=applied.length+':'+applied.reduce((s,t)=>s+t.grossPnl,0).toFixed(0);
  useEffect(()=>{
    drawChart(applied);
    return()=>{chartRef.current?.destroy();chartRef.current=null;};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[filteredKey]);

  const apply=(f=from,t=to,tk=ticker,df=dateField)=>{
    setFrom(f);setTo(t);setTicker(tk);setDateField(df);
    journalPutFilter({from:f,to:t,ticker:tk,dateField:df}).catch(()=>{});
  };
  const reset=()=>{
    setFrom(minDate);setTo(maxDate);setTicker('');setDateField('close');
    journalPutFilter({from:minDate,to:maxDate,ticker:'',dateField:'close'}).catch(()=>{});
  };
  const m=calcMetrics(applied, totalComm); // totalComm fallback for legacy PDF imports
  const byTk={};for(const t of applied){if(!byTk[t.underlying])byTk[t.underlying]=[];byTk[t.underlying].push(t);}
  const tkRows=Object.entries(byTk).sort((a,b)=>calcMetrics(b[1]).totalPnl-calcMetrics(a[1]).totalPnl);
  const byD={},byW={};for(const t of applied){byD[t.closeDate]=(byD[t.closeDate]||[]).concat(t);const w=isoWeek(t.closeDate);byW[w]=(byW[w]||[]).concat(t);}
  return(<div>
    <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap',marginBottom:14,fontFamily:"'IBM Plex Mono',monospace",fontSize:11}}>
      <label style={{color:'var(--muted)'}}>Date</label>
      <select value={dateField} onChange={e=>setDateField(e.target.value)} style={{...inSt,minWidth:80}}>
        <option value="close">Close</option>
        <option value="open">Open</option>
      </select>
      <label style={{color:'var(--muted)'}}>From</label><input type="date" value={from} onChange={e=>setFrom(e.target.value)} style={inSt}/>
      <label style={{color:'var(--muted)'}}>To</label><input type="date" value={to} onChange={e=>setTo(e.target.value)} style={inSt}/>
      <label style={{color:'var(--muted)'}}>Symbol</label>
      <select value={ticker} onChange={e=>setTicker(e.target.value)} style={inSt}>
        <option value="">All</option>{tickers.map(t=><option key={t} value={t}>{t}</option>)}
      </select>
      <button onClick={()=>apply(from,to,ticker,dateField)} style={btnSt}>Apply</button>
      <button onClick={reset} style={{...btnSt,background:'transparent',color:'var(--muted)',border:'1px solid var(--border)'}}>Reset</button>
    </div>
    {m?<StatsRow m={m} totalComm={m?.totalComm ?? totalComm}/>:<div style={{color:'var(--muted)',fontSize:12,marginBottom:16}}>No trades match filters</div>}
    <div style={{background:'var(--bg-elev)',border:'1px solid var(--border)',borderRadius:8,padding:14,marginBottom:16}}>
      <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:9,textTransform:'uppercase',letterSpacing:'.1em',color:'var(--muted)',marginBottom:10}}>Equity Curve — Filtered</div>
      <div style={{height:140}}><canvas ref={perfCanvasRef}/></div>
    </div>
    <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:9,textTransform:'uppercase',letterSpacing:'.1em',color:'var(--muted)',marginBottom:8}}>By Symbol</div>
    <div style={{border:'1px solid var(--border)',borderRadius:8,overflow:'hidden',marginBottom:16}}>
      <table style={{width:'100%',borderCollapse:'collapse'}}>
        <thead><tr>{['Symbol','Type','Trades','Win%','Net P&L','Avg Win','Avg Loss','P.Factor','Expectancy'].map((s,i)=><th key={i} style={thSt}>{s}</th>)}</tr></thead>
        <tbody>{tkRows.map(([sym,ts])=>{const tm=calcMetrics(ts);const types=[...new Set(ts.map(t=>t.type))];return(
          <tr key={sym} style={{borderTop:'1px solid var(--border)'}}>
            <td style={tdSt}><b>{sym}</b></td>
            <td style={tdSt}>{types.map(t=><TypeBadge key={t} type={t}/>)}</td>
            <td style={{...tdSt,textAlign:'right'}}>{tm.total}</td>
            <td style={{...tdSt,textAlign:'right',color:tm.winRate>=0.5?C.up:C.dn}}>{pct(tm.winRate)}</td>
            <td style={{...tdSt,textAlign:'right',color:tm.totalPnl>=0?C.up:C.dn}}>{fmtD(tm.totalPnl)}</td>
            <td style={{...tdSt,textAlign:'right',color:C.up}}>+${tm.avgWin.toFixed(2)}</td>
            <td style={{...tdSt,textAlign:'right',color:C.dn}}>-${tm.avgLoss.toFixed(2)}</td>
            <td style={{...tdSt,textAlign:'right',color:tm.profitFactor>=1?C.up:C.dn}}>{fmtPf(tm.profitFactor)}</td>
            <td style={{...tdSt,textAlign:'right',color:tm.expectancy>=0?C.up:C.dn}}>{fmtD(tm.expectancy)}</td>
          </tr>);})}</tbody>
      </table>
    </div>
    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
      <div><div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:9,textTransform:'uppercase',letterSpacing:'.1em',color:'var(--muted)',marginBottom:8}}>By Day</div><PnlTable rows={Object.entries(byD).sort((a,b)=>a[0].localeCompare(b[0]))}/></div>
      <div><div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:9,textTransform:'uppercase',letterSpacing:'.1em',color:'var(--muted)',marginBottom:8}}>By Week</div><PnlTable rows={Object.entries(byW).sort((a,b)=>a[0].localeCompare(b[0]))}/></div>
    </div>
    <RegimeBucket byDay={byD}/>
  </div>);}

// ── P&L by Regime ─────────────────────────────────────────────────────────────
// Lets you tag each trading day as Trend / Range / Mixed, then buckets realized
// P&L by regime — proves (or disproves) the "I lose on chop days" thesis over time.
// Tags persist in localStorage keyed by date.
const REGIME_TAGS = [
  { key:'trend', label:'Trend', color:C.up },
  { key:'range', label:'Range/Chop', color:C.dn },
  { key:'mixed', label:'Mixed', color:C.wa },
  { key:'',      label:'Untagged', color:C.mu },
];
function loadRegimeTags(){ try{ return JSON.parse(localStorage.getItem('od:regimeTags')||'{}'); }catch{ return {}; } }
function saveRegimeTagsLocal(t){ try{ localStorage.setItem('od:regimeTags', JSON.stringify(t)); }catch{} }

function RegimeBucket({byDay}){
  const [tags,setTags]=useState(()=>loadRegimeTags());
  const [synced,setSynced]=useState(false);
  const days=Object.entries(byDay).map(([d,ts])=>[d, ts.reduce((s,t)=>s+t.grossPnl,0)]).sort((a,b)=>a[0].localeCompare(b[0]));

  // Load from KV on mount — KV is source of truth, falls back to localStorage
  useEffect(()=>{
    let cancelled=false;
    journalGetRegimes().then(data=>{
      if(cancelled) return;
      const kvTags=data?.tags||{};
      // Merge: KV wins, but keep any local-only tags not yet synced
      const local=loadRegimeTags();
      const merged={...local,...kvTags};
      setTags(merged);
      saveRegimeTagsLocal(merged);
      setSynced(true);
    }).catch(()=>{ setSynced(true); }); // offline — keep localStorage
    return()=>{cancelled=true;};
  },[]);

  const setTag=(date,regime)=>{
    const next={...tags}; if(regime) next[date]=regime; else delete next[date];
    setTags(next);
    saveRegimeTagsLocal(next);
    journalPutRegimes(next).catch(()=>{}); // fire-and-forget to KV
  };

  // Bucket P&L by regime
  const buckets={};
  for(const [date,pnl] of days){
    const r=tags[date]||'';
    if(!buckets[r]) buckets[r]={pnl:0,days:0,wins:0};
    buckets[r].pnl+=pnl; buckets[r].days++; if(pnl>=0) buckets[r].wins++;
  }

  return(<div style={{marginTop:16}}>
    <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:9,textTransform:'uppercase',letterSpacing:'.1em',color:'var(--muted)',marginBottom:8}}>
      P&amp;L by Regime <span style={{color:'var(--faint)',textTransform:'none',letterSpacing:0}}>— tag each day to bucket performance{synced?' · synced':' · local'}</span>
    </div>

    {/* Bucket summary cards */}
    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:8,marginBottom:12}}>
      {REGIME_TAGS.map(rt=>{
        const b=buckets[rt.key]; if(!b||b.days===0) return null;
        const avg=b.pnl/b.days;
        return(<div key={rt.key} style={{padding:'10px 12px',borderRadius:6,border:`1px solid ${rt.color}`,background:rt.color+'0c'}}>
          <div style={{fontSize:9,textTransform:'uppercase',letterSpacing:'.06em',color:rt.color,marginBottom:3}}>{rt.label}</div>
          <div style={{fontSize:18,fontWeight:700,color:b.pnl>=0?C.up:C.dn,fontFamily:'monospace'}}>{fmtD(b.pnl)}</div>
          <div style={{fontSize:9,color:'var(--faint)',marginTop:2}}>
            {b.days}d · {b.wins}/{b.days} green · avg {fmtD(avg)}
          </div>
        </div>);
      })}
    </div>

    {/* Per-day tagging table */}
    <div style={{border:'1px solid var(--border)',borderRadius:8,overflow:'hidden',maxHeight:280,overflowY:'auto'}}>
      <table style={{width:'100%',borderCollapse:'collapse'}}>
        <thead><tr>{['Date','P&L','Regime tag'].map((s,i)=><th key={i} style={thSt}>{s}</th>)}</tr></thead>
        <tbody>{days.slice().reverse().map(([date,pnl])=>(
          <tr key={date} style={{borderTop:'1px solid var(--border)'}}>
            <td style={tdSt}>{date}</td>
            <td style={{...tdSt,textAlign:'right',color:pnl>=0?C.up:C.dn,fontFamily:'monospace'}}>{fmtD(pnl)}</td>
            <td style={{...tdSt,textAlign:'right'}}>
              <div style={{display:'inline-flex',gap:3}}>
                {REGIME_TAGS.filter(rt=>rt.key).map(rt=>(
                  <button key={rt.key} onClick={()=>setTag(date, tags[date]===rt.key?'':rt.key)}
                    title={rt.label}
                    style={{fontSize:9,padding:'2px 7px',borderRadius:3,cursor:'pointer',fontWeight:600,
                      border:`1px solid ${tags[date]===rt.key?rt.color:'var(--border)'}`,
                      background:tags[date]===rt.key?rt.color:'transparent',
                      color:tags[date]===rt.key?'var(--bg)':'var(--muted)'}}>
                    {rt.label.split('/')[0]}
                  </button>
                ))}
              </div>
            </td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  </div>);}


// ── Calendar Tab ──────────────────────────────────────────────────────────────
const REGIME_COLORS = {
  trend: { bg: '#22c55e18', border: '#22c55e40', text: '#22c55e', label: 'Trend' },
  range: { bg: '#818cf818', border: '#818cf840', text: '#818cf8', label: 'Range' },
  mixed: { bg: '#f59e0b18', border: '#f59e0b40', text: '#f59e0b', label: 'Mixed' },
};

function RegimePill({regime}) {
  if (!regime) return null;
  const c = REGIME_COLORS[regime.regime] ?? { bg:'var(--border)', border:'var(--faint)', text:'var(--faint)', label: regime.label||'?' };
  return (
    <span style={{ fontSize:7, fontWeight:700, letterSpacing:'.06em', padding:'1px 4px',
      borderRadius:2, border:`1px solid ${c.border}`, background:c.bg, color:c.text,
      textTransform:'uppercase', whiteSpace:'nowrap', lineHeight:1.4 }}>
      {regime.label ?? c.label}
    </span>
  );
}

function CalendarTab({trades, regimes}) {
  const [viewDate,setViewDate]=useState(()=>{
    const latest=trades.map(t=>t.closeDate).filter(Boolean).sort().pop();
    return latest ? new Date(latest.replace(/-/g,'/')) : new Date();
  });
  const [selected,setSelected]=useState(null);

  const byDate={};
  for(const t of trades){
    if(!t.closeDate) continue;
    if(!byDate[t.closeDate]) byDate[t.closeDate]=[];
    byDate[t.closeDate].push(t);
  }

  const year=viewDate.getFullYear(), month=viewDate.getMonth();
  const firstDOW=new Date(year,month,1).getDay();
  const daysInMonth=new Date(year,month+1,0).getDate();
  const monthLabel=new Date(year,month,1).toLocaleString('default',{month:'long',year:'numeric'});
  const ds=(d)=>`${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;

  const monthPrefix=`${year}-${String(month+1).padStart(2,'0')}`;
  const monthTrades=Object.entries(byDate).filter(([k])=>k.startsWith(monthPrefix));
  const monthGross=monthTrades.reduce((s,[,ts])=>s+ts.reduce((a,t)=>a+(t.grossPnl||0),0),0);
  const monthComm =monthTrades.reduce((s,[,ts])=>s+ts.reduce((a,t)=>a+(t.commPnl||0),0),0);
  const monthPnl  =monthGross+monthComm; // net after commission
  const greenDays=monthTrades.filter(([,ts])=>ts.reduce((a,t)=>a+(t.grossPnl||0)+(t.commPnl||0),0)>0).length;
  const redDays  =monthTrades.filter(([,ts])=>ts.reduce((a,t)=>a+(t.grossPnl||0)+(t.commPnl||0),0)<0).length;

  const selTrades = selected ? (byDate[selected]??[]) : [];
  const selRegime = selected ? regimes[selected] : null;

  const navigateMonth=(dir)=>{ setViewDate(new Date(year,month+dir,1)); setSelected(null); };

  return (
    <div style={{fontFamily:"'IBM Plex Mono',monospace"}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12,flexWrap:'wrap',gap:8}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <button onClick={()=>navigateMonth(-1)} style={calBtn}>&#8249; Prev</button>
          <span style={{fontSize:13,fontWeight:700,color:'var(--text)',minWidth:160,textAlign:'center'}}>{monthLabel}</span>
          <button onClick={()=>navigateMonth(1)} style={calBtn}>Next &#8250;</button>
        </div>
        <div style={{display:'flex',gap:16,fontSize:10,color:'var(--muted)'}}>
          <span style={{color:'var(--up)',fontWeight:700}}>{greenDays} green days</span>
          <span style={{color:'var(--down)',fontWeight:700}}>{redDays} red days</span>
          <span style={{color:monthPnl>=0?'var(--up)':'var(--down)',fontWeight:700}}>
            {monthPnl>=0?'+':''}{monthPnl.toFixed(0)} net
          </span>
          {monthComm<-0.01&&(
            <span style={{color:'var(--faint)',fontSize:9}}>
              gross {monthGross>=0?'+':''}{monthGross.toFixed(0)} · comm {monthComm.toFixed(0)}
            </span>
          )}
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:2,marginBottom:2}}>
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d=>(
          <div key={d} style={{fontSize:9,color:'var(--faint)',textAlign:'center',padding:'3px 0',letterSpacing:'.06em'}}>{d}</div>
        ))}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:2}}>
        {Array.from({length:firstDOW},(_,i)=><div key={"e"+i}/>)}
        {Array.from({length:daysInMonth},(_,i)=>{
          const day=i+1, date=ds(day);
          const dayTrades=byDate[date]??[];
          const gross=dayTrades.reduce((s,t)=>s+(t.grossPnl||0),0);
          const comm =dayTrades.reduce((s,t)=>s+(t.commPnl||0),0);
          const pnl  =gross+comm; // net after commission
          const hasComm = comm < -0.01;
          const hasTrades=dayTrades.length>0;
          const regime=regimes[date];
          const isGreen=hasTrades&&pnl>0, isRed=hasTrades&&pnl<0;
          const isSel=selected===date;
          const isWeekend=[0,6].includes(new Date(year,month,day).getDay());
          return (
            <div key={day} onClick={()=>setSelected(isSel?null:date)}
              style={{
                minHeight:72,padding:'5px 6px',borderRadius:5,
                cursor:hasTrades||regime?'pointer':'default',
                border:`1px solid ${isSel?'var(--accent)':isGreen?'#22c55e50':isRed?'#ef444450':'var(--border)'}`,
                background: isSel?'var(--accent)18' : isGreen?'#22c55e0d' : isRed?'#ef44440d' : 'var(--bg)',
                display:'flex',flexDirection:'column',gap:1,
                opacity: isWeekend&&!hasTrades&&!regime ? 0.35 : 1,
                transition:'border-color .15s',
              }}>
              <div style={{fontSize:10,fontWeight:isSel?800:500,color:isSel?'var(--accent)':hasTrades?'var(--text)':'var(--faint)'}}>{day}</div>
              {hasTrades&&(
                <div style={{fontSize:12,fontWeight:800,color:isGreen?'var(--up)':'var(--down)',lineHeight:1.1}}>
                  {pnl>=0?'+':''}{pnl.toFixed(0)}
                </div>
              )}
              {hasTrades&&hasComm&&(
                <div style={{fontSize:8,color:'var(--down)',opacity:0.7,lineHeight:1}}>
                  comm {comm.toFixed(0)}
                </div>
              )}
              {hasTrades&&(
                <div style={{fontSize:8,color:'var(--faint)'}}>{dayTrades.length}t</div>
              )}
              {regime&&<RegimePill regime={regime}/>}
            </div>
          );
        })}
      </div>

      {selected&&(
        <div style={{marginTop:14,border:'1px solid var(--border)',borderRadius:8,overflow:'hidden'}}>
          <div style={{padding:'8px 14px',background:'var(--bg-elev,var(--bg))',borderBottom:'1px solid var(--border)',
            display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:8}}>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <span style={{fontSize:12,fontWeight:700,color:'var(--text)'}}>{selected}</span>
              {selRegime&&<RegimePill regime={selRegime}/>}
              {selRegime?.confidence!=null&&(
                <span style={{fontSize:9,color:'var(--faint)'}}>confidence {selRegime.confidence}%</span>
              )}
              {selRegime?.adx!=null&&(
                <span style={{fontSize:9,color:'var(--faint)'}}>ADX {selRegime.adx}</span>
              )}
            </div>
            {selTrades.length>0&&(()=>{
              const gross=selTrades.reduce((s,t)=>s+(t.grossPnl||0),0);
              const comm =selTrades.reduce((s,t)=>s+(t.commPnl||0),0);
              const total=gross+comm;
              return <div style={{textAlign:'right'}}>
                <span style={{fontSize:13,fontWeight:800,color:total>=0?'var(--up)':'var(--down)'}}>
                  {total>=0?'+':''}{total.toFixed(2)} net
                </span>
                {comm<-0.01&&<div style={{fontSize:9,color:'var(--faint)'}}>
                  gross {gross>=0?'+':''}{gross.toFixed(2)} · comm {comm.toFixed(2)}
                </div>}
              </div>;
            })()}
          </div>
          {selTrades.length===0?(
            <div style={{padding:'14px',fontSize:11,color:'var(--faint)',textAlign:'center'}}>
              No trades on this day.{selRegime?' Day type: '+selRegime.label+'.':''}
            </div>
          ):(
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr>
                {['Symbol','Type','Open','Close','P&L','Legs'].map((h,i)=>(
                  <th key={i} style={{...thSt}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>{selTrades.map((t,i)=>(
                <tr key={t.key||i} style={{borderTop:'1px solid var(--border)'}}>
                  <td style={tdSt}><b>{t.underlying}</b></td>
                  <td style={tdSt}><TypeBadge type={t.type}/></td>
                  <td style={tdSt}>{t.tradeDate}</td>
                  <td style={tdSt}>{t.closeDate}</td>
                  <td style={{...tdSt,textAlign:'right',color:t.grossPnl>=0?C.up:C.dn,fontWeight:700}}>
                    {t.grossPnl>=0?'+':''}{t.grossPnl?.toFixed(2)}
                  </td>
                  <td style={{...tdSt,color:'var(--faint)'}}>{t.legs?.length??1}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
const calBtn={fontSize:10,padding:'4px 10px',border:'1px solid var(--border)',borderRadius:4,
  background:'transparent',color:'var(--muted)',cursor:'pointer',fontFamily:"'IBM Plex Mono',monospace"};

// ── Trades Tab ───────────────────────────────────────────────────────────────
function TradesTab({trades, onRemove, records=[], regimes={}}){
  const [search,setSearch]=useState('');const [typeF,setTypeF]=useState('');const [resultF,setResultF]=useState('');
  const [sortCol,setSortCol]=useState('closeDate');const [sortDir,setSortDir]=useState(-1);
  let ts=trades;
  if(search)ts=ts.filter(t=>(t.underlying+t.type+(t.spreadType||'')).toLowerCase().includes(search.toLowerCase()));
  if(typeF)ts=ts.filter(t=>t.type===typeF);
  if(resultF)ts=ts.filter(t=>resultF==='win'?t.isWin:!t.isWin);
  ts=[...ts].sort((a,b)=>{
    const av=a[sortCol]??'', bv=b[sortCol]??'';
    if(typeof av==='number'&&typeof bv==='number') return (av-bv)*sortDir;
    return String(av).localeCompare(String(bv))*sortDir;
  });
  const sort=col=>{if(sortCol===col)setSortDir(d=>-d);else{setSortCol(col);setSortDir(1);}};
  const Th=({col,label,right=false})=>(
    <th onClick={()=>sort(col)} style={{...thSt,cursor:'pointer',textAlign:right?'right':'left'}}>
      {label}{sortCol===col?(sortDir>0?' ↑':' ↓'):''}
    </th>);
  const totalComm=records.filter(r=>(r.added||0)>0).reduce((s,r)=>s+(r.totalComm||0),0);
  const avgComm=trades.length&&totalComm?totalComm/trades.length:0;
  return(<div>
    <div style={{display:'flex',gap:10,alignItems:'center',marginBottom:12,flexWrap:'wrap',fontFamily:"'IBM Plex Mono',monospace",fontSize:11}}>
      <input placeholder="Search symbol, spread…" value={search} onChange={e=>setSearch(e.target.value)} style={inSt}/>
      <select value={typeF} onChange={e=>setTypeF(e.target.value)} style={inSt}>
        <option value="">All types</option><option value="SPREAD">Spread</option>
        <option value="CALL">Single Call</option><option value="PUT">Single Put</option><option value="STOCK">Stock</option>
      </select>
      <select value={resultF} onChange={e=>setResultF(e.target.value)} style={inSt}>
        <option value="">All results</option><option value="win">Win</option><option value="loss">Loss</option>
      </select>
      <span style={{color:'var(--muted)',marginLeft:'auto'}}>{ts.length} trades</span>
      {totalComm<0&&<span style={{color:C.dn,fontSize:10}}>Total comm: {fmtD(totalComm)}</span>}
    </div>
    <div style={{border:'1px solid var(--border)',borderRadius:8,overflow:'hidden'}}>
      <table style={{width:'100%',borderCollapse:'collapse'}}>
        <thead><tr>
          <Th col="closeDate"  label="Close"/>
          <Th col="tradeDate"  label="Open"/>
          <Th col="underlying" label="Symbol"/>
          <th style={thSt}>Type / Spread</th>
          <Th col="strikeRange" label="Strikes"/>
          <Th col="expiry"     label="Expiry"/>
          <Th col="soldQty"    label="Qty"       right/>
          <Th col="grossPnl"   label="Gross P&L" right/>
          <th style={{...thSt,textAlign:'right'}}>Est. Comm</th>
          <Th col="isWin"      label="Result"/>
          <th style={thSt}></th>
        </tr></thead>
        <tbody>{ts.map((t,i)=>{
          const legs=(t.legs?.length||1);
          const estComm=avgComm?fmtD(avgComm*legs):'—';
          // Show a date-group header row when the close date changes
          const prevDate=i>0?ts[i-1].closeDate:null;
          const showDateRow=t.closeDate&&t.closeDate!==prevDate;
          const regime=showDateRow?regimes[t.closeDate]:null;
          return(<>
          {showDateRow&&(
            <tr key={"dr"+t.closeDate} style={{borderTop:'2px solid var(--border)',background:'var(--bg-elev,var(--bg))'}}>
              <td colSpan={11} style={{padding:'5px 10px'}}>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <span style={{fontSize:10,fontWeight:700,color:'var(--muted)',fontFamily:"'IBM Plex Mono',monospace"}}>{t.closeDate}</span>
                  {regime&&<RegimePill regime={regime}/>}
                  {regime?.confidence!=null&&<span style={{fontSize:9,color:'var(--faint)'}}>confidence {regime.confidence}%</span>}
                </div>
              </td>
            </tr>
          )}
          <tr key={t.key} style={{borderTop:'1px solid var(--border)'}}>
            <td style={tdSt}>{t.closeDate||'—'}</td>
            <td style={{...tdSt,color:C.mu}}>{t.tradeDate||'—'}</td>
            <td style={tdSt}><b>{t.underlying}</b></td>
            <td style={tdSt}><TypeBadge type={t.type} spreadType={t.spreadType}/></td>
            <td style={tdSt}>{t.strikeRange||(t.strike?'$'+t.strike.toFixed(1):'—')}</td>
            <td style={tdSt}>{t.expiry||'—'}</td>
            <td style={{...tdSt,textAlign:'right'}}>{t.soldQty||t.boughtQty}</td>
            <td style={{...tdSt,textAlign:'right',color:t.grossPnl>=0?C.up:C.dn,fontWeight:600}}>{fmtD(t.grossPnl)}</td>
            <td style={{...tdSt,textAlign:'right',color:C.mu,fontSize:10}}>{estComm}</td>
            <td style={tdSt}><span style={{padding:'2px 5px',borderRadius:3,fontSize:9,fontWeight:700,textTransform:'uppercase',background:t.isWin?C.upA:C.dnA,color:t.isWin?C.up:C.dn}}>{t.isWin?'WIN':'LOSS'}</span></td>
            <td style={{...tdSt,textAlign:'center',width:32}}>
              <button onClick={()=>onRemove(t.key)} title="Remove trade"
                style={{background:'transparent',border:'none',color:C.mu,cursor:'pointer',fontSize:13,lineHeight:1,padding:'2px 4px',borderRadius:3,transition:'color .15s'}}
                onMouseEnter={e=>e.currentTarget.style.color=C.dn}
                onMouseLeave={e=>e.currentTarget.style.color=C.mu}>✕</button>
            </td>
          </tr></>);})}
        </tbody>
      </table>
    </div>
  </div>);}
// ── Records Tab ──────────────────────────────────────────────────────────────
function RecordsTab({trades,records,onDelete,onToggleLock,onClear}){
  const [confirming, setConfirming] = useState(false);
  const accounts=[...new Set(records.map(r=>r.account))];
  const lockedCount = records.filter(r=>r.locked).length;
  const unlockedTrades = trades.filter(t=>!records.find(r=>r.id===t.importId&&r.locked));
  return(<div>
    {accounts.map(acc=>{const ts=trades.filter(t=>t.account===acc);const m=calcMetrics(ts);const rec=records.find(r=>r.account===acc);return(
      <div key={acc} style={{background:'var(--bg-elev)',border:'1px solid var(--border)',borderRadius:8,padding:14,marginBottom:16}}>
        <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:14,fontWeight:700,color:'var(--accent)',marginBottom:2}}>{acc}</div>
        <div style={{color:'var(--muted)',fontSize:11,marginBottom:m?12:0}}>{rec?.name}</div>
        {m&&(<div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
          <StatCard label="Trades" value={m.total}/>
          <StatCard label="Net P&L (gross)" value={fmtD(m.grossTotalPnlNoComm||m.totalPnl)} color={(m.grossTotalPnlNoComm||m.totalPnl)>=0?C.up:C.dn}/>
          <StatCard label="Win Rate" value={pct(m.winRate)} color={C.ac}/>
          <StatCard label="Profit Factor" value={fmtPf(m.profitFactor)} color={m.profitFactor>=1?C.up:C.dn}/>
        </div>)}
      </div>);})}
    <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:9,textTransform:'uppercase',letterSpacing:'.1em',color:'var(--muted)',marginBottom:8}}>Import History</div>
    <div style={{border:'1px solid var(--border)',borderRadius:8,overflow:'hidden',marginBottom:16}}>
      <table style={{width:'100%',borderCollapse:'collapse'}}>
        <thead><tr>{['Imported','File','Account','Added','Trades','Lock',''].map((s,i)=><th key={i} style={thSt}>{s}</th>)}</tr></thead>
        <tbody>{records.map(r=>{
          const linked=trades.filter(t=>t.importId===r.id).length;
          const isLocked=!!r.locked;
          return(
          <tr key={r.id} style={{borderTop:'1px solid var(--border)',opacity:isLocked?1:0.92}}>
            <td style={tdSt}>{r.importedAt?.slice(0,16).replace('T',' ')}</td>
            <td style={tdSt}>{r.filename}</td>
            <td style={tdSt}>{r.account}</td>
            <td style={{...tdSt,color:C.up}}>+{r.added}</td>
            <td style={{...tdSt,color:linked>0?C.mu:'var(--muted)'}}>
              {linked>0?linked:'—'}
            </td>
            {/* Lock toggle */}
            <td style={tdSt}>
              <button
                onClick={()=>onToggleLock(r.id)}
                title={isLocked?'Locked — click to unlock':'Unlocked — click to lock'}
                style={{
                  padding:'2px 8px',borderRadius:3,fontSize:10,cursor:'pointer',
                  fontFamily:"'IBM Plex Mono',monospace",border:'none',
                  background: isLocked?'rgba(255,184,0,0.18)':'var(--bg)',
                  color: isLocked?'var(--warn)':'var(--muted)',
                  outline: isLocked?'1px solid var(--warn)':'1px solid var(--border)',
                }}>
                {isLocked?'🔒 Locked':'🔓 Lock'}
              </button>
            </td>
            {/* Delete — disabled if locked */}
            <td style={tdSt}>
              <button
                onClick={()=>{
                  if(isLocked){alert('Unlock this record first before deleting.');return;}
                  if(!confirm(`Delete this import record and its ${linked} linked trade${linked!==1?'s':''}?`))return;
                  onDelete(r.id);
                }}
                disabled={isLocked}
                title={isLocked?'Unlock first to delete':'Delete record + linked trades'}
                style={{padding:'2px 8px',background:'transparent',border:`1px solid ${isLocked?'var(--border)':'var(--down)'}`,
                  color:isLocked?'var(--muted)':'var(--down)',borderRadius:3,
                  fontFamily:"'IBM Plex Mono',monospace",fontSize:9,
                  cursor:isLocked?'not-allowed':'pointer',opacity:isLocked?0.4:1}}>
                Delete
              </button>
            </td>
          </tr>);
        })}</tbody>
      </table>
    </div>

    {lockedCount>0&&(
      <div style={{background:'rgba(255,184,0,0.08)',border:'1px solid var(--warn)',borderRadius:6,
        padding:'8px 14px',marginBottom:12,fontSize:11,fontFamily:"'IBM Plex Mono',monospace",color:'var(--warn)'}}>
        🔒 {lockedCount} record{lockedCount!==1?'s':''} locked — Reset will preserve {lockedCount===records.length?'all':'these'} import{lockedCount!==1?'s':''} and their linked trades.
      </div>
    )}

    {/* Reset All Trades — two-step confirm */}
    {!confirming ? (
      <button onClick={() => setConfirming(true)}
        style={{...btnSt,background:'transparent',border:'1px solid var(--down)',color:'var(--down)'}}>
        ✕ Reset All Trades
      </button>
    ) : (
      <div style={{background:'rgba(248,81,73,0.08)',border:'1px solid var(--down)',
        borderRadius:7,padding:'12px 14px',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
        <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:'var(--down)',fontWeight:700}}>
          ⚠ This will permanently delete {unlockedTrades.length} unlocked trade{unlockedTrades.length!==1?'s':''} and {records.length-lockedCount} import record{records.length-lockedCount!==1?'s':''}.
          {lockedCount>0?` ${lockedCount} locked record${lockedCount!==1?'s':''} and their trades will be preserved.`:' Cannot be undone.'}
        </span>
        <div style={{display:'flex',gap:8,marginLeft:'auto'}}>
          <button onClick={() => setConfirming(false)}
            style={{...btnSt,background:'transparent',border:'1px solid var(--border)',color:'var(--muted)'}}>
            Cancel
          </button>
          <button onClick={() => { setConfirming(false); onClear(); }}
            style={{...btnSt,background:'var(--down)',color:'#fff',border:'none'}}>
            Confirm Reset
          </button>
        </div>
      </div>
    )}
  </div>);}

// ── Session Notes Log Tab ────────────────────────────────────────────────────

function SessionNotesLogTab() {
  const [log,     setLog]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncErr, setSyncErr] = useState(false);

  useEffect(() => {
    sessionLogGet()
      .then(d => { setLog(d.notes || []); setLoading(false); })
      .catch(() => { setSyncErr(true); setLoading(false); });
  }, []);

  const deleteEntry = async (id) => {
    const updated = log.filter(n => n.id !== id);
    setLog(updated);
    sessionLogPut(updated).catch(() => setSyncErr(true));
  };

  const clearAll = async () => {
    if (!confirm('Clear all session notes from the journal?')) return;
    setLog([]);
    sessionLogPut([]).catch(() => setSyncErr(true));
  };

  if (loading) return (
    <div className="empty" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: 'var(--muted)', padding: 32, textAlign: 'center' }}>
      ⟳ Loading session notes…
    </div>
  );

  if (!log.length) return (
    <div className="empty" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: 'var(--muted)', padding: 32, textAlign: 'center' }}>
      No session notes recorded yet.<br />
      <span style={{ fontSize: 10, opacity: 0.6 }}>Notes are saved automatically when you set a session plan on the Dashboard.</span>
      {syncErr && <div style={{ marginTop: 8, color: 'var(--down)', fontSize: 10 }}>⚠ Sync error — check connection</div>}
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--muted)' }}>
          {log.length} session entries
        </span>
        {syncErr && <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, color: 'var(--down)' }}>⚠ sync error</span>}
        <button onClick={clearAll} style={{ marginLeft: 'auto', padding: '3px 10px', background: 'transparent', border: '1px solid var(--down)', color: 'var(--down)', borderRadius: 4, fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, cursor: 'pointer' }}>
          Clear All
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {log.map(n => (
          <div key={n.id} style={{ background: 'var(--bg-elev)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 700, color: 'var(--accent)' }}>
                {n.createdAt ? new Date(n.createdAt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
              </span>
              <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, color: 'var(--muted)' }}>
                {n.createdAt ? new Date(n.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : ''}
                {n.updatedAt && n.updatedAt !== n.createdAt ? ` · edited ${new Date(n.updatedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}` : ''}
              </span>
              <button onClick={() => deleteEntry(n.id)} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 13 }} title="Delete entry">✕</button>
            </div>
            <pre style={{ margin: 0, fontFamily: "'IBM Plex Mono',monospace", fontSize: 11, lineHeight: 1.65, color: 'var(--text)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {n.text}
            </pre>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Root ─────────────────────────────────────────────────────────────────────
export default function TradeJournal(){
  const [tab,setTab]=useState(()=>localStorage.getItem('od:journalTab')||'overview');
  const switchJournalTab = (t) => { setTab(t); localStorage.setItem('od:journalTab', t); };
  const [trades,setTrades]=useState([]);
  const [records,setRecords]=useState([]);
  const [regimes,setRegimes]=useState({});
  // Overview start date — filters data shown in Overview and Calendar tabs
  const [startDate, setStartDate] = useState(() => localStorage.getItem('od:journalStart') || '');
  const setJournalStart = (d) => {
    setStartDate(d);
    localStorage.setItem('od:journalStart', d);          // instant local update
    journalPutSettings({ startDate: d }).catch(() => {}); // persist to KV
  };  // { "YYYY-MM-DD": { regime, label, color, ... } }
  const [syncState,setSyncState]=useState('loading');
  const [importLog,setImportLog]=useState([]);
  const [logVisible,setLogVisible]=useState(false);

  useEffect(()=>{(async()=>{
    try{
      const [td,rd,reg,jsettings]=await Promise.all([journalGetTrades(),journalGetRecords(),regimeDailyGet(),journalGetSettings().catch(()=>({}))]);
      const loaded=(td.trades||[]).map(t=>({
        ...t,
        tradeDate: t.tradeDate==='?'?'':t.tradeDate,
        closeDate:  t.closeDate==='?'?'':t.closeDate,
      }));
      setTrades(loaded);setRecords(rd.records||[]);
      setRegimes(reg && typeof reg === 'object' ? reg : {});
      // Restore start date from KV, falling back to localStorage
      if (jsettings?.startDate) {
        setStartDate(jsettings.startDate);
        localStorage.setItem('od:journalStart', jsettings.startDate);
      }
      setSyncState('ok');
    }
    catch{setSyncState('err');}
  })();},[]);

  const syncToServer=useCallback(async(nt,nr)=>{
    setSyncState('syncing');
    try{await Promise.all([journalPutTrades(nt),journalPutRecords(nr)]);setSyncState('ok');}
    catch{setSyncState('err');}
  },[]);

  const removeTrade=useCallback(async(key)=>{
    const newTrades=trades.filter(t=>t.key!==key);
    setTrades(newTrades);
    await syncToServer(newTrades,records);
  },[trades,records,syncToServer]);

  const handleFlexXml = useCallback(async(xmlText) => {
    if(!xmlText) return;
    setLogVisible(true);
    const log=[];const addLog=(msg,cls='info')=>{log.push({msg,cls});setImportLog([...log]);};
    addLog('⚡ IBKR Flex XML received');
    try {
      // Auto-detect report type via the queryName type attribute or element names:
      //   type="TCF" = Trade Confirmation Flex query
      //   type="AF"  = Activity Flex query
      const isTCF = /type=["']TCF["']/.test(xmlText);
      const isConfirmation = isTCF || xmlText.includes('<TradeConfirm ') || xmlText.includes('<TradeConfirm>');
      if (isConfirmation) {
        addLog('   Detected: Trade Confirmation report (TCF)');
        // Check for empty report before parsing
        if (!xmlText.includes('<TradeConfirm ') && !xmlText.includes('<TradeConfirm>')) {
          addLog('⚠ No TradeConfirm rows in this XML — the report is empty for this date range.');
          addLog('   This is a Trade Confirmation query. Run your Activity Flex query instead for closed trade P&L.');
          addLog('❌ No trades to import', 'err');
          return;
        }
      } else {
        addLog('   Detected: Activity (Trades) report (AF)');
      }
      const result = isConfirmation
        ? parseTradeConfirmXML(xmlText, msg => addLog('   '+msg))
        : parseFlexXML(xmlText, msg => addLog('   '+msg));
      if(!result){addLog('❌ Parse failed — check your Flex Query settings','err');return;}

      const openKey = (leg) => `${leg.account||''}|${leg.contract||leg.key||''}|${leg.tradeDate||''}`;
      const existingOpenKeys = new Map();
      trades.forEach((t, idx) => { (t.legs||[t]).forEach(l => existingOpenKeys.set(openKey(l), idx)); });

      const replacedIdx = new Set(); let added=0, replaced=0; const incoming=[];
      for(const t of result.trades){
        const legs=t.legs||[t];
        const matches=legs.map(l=>existingOpenKeys.get(openKey(l))).filter(i=>i!==undefined);
        if(matches.length>0){ matches.forEach(i=>replacedIdx.add(i)); replaced++; }
        else { added++; }
        incoming.push(t);
      }
      const recId = Date.now();
      const kept=trades.filter((_,i)=>!replacedIdx.has(i));
      const newTrades=[...kept,...incoming.map(t=>({...t,importId:recId}))];
      const rec={id:recId,filename:'IBKR Flex',account:result.account,name:result.name,
        importedAt:new Date().toISOString(),found:result.trades.length,added,replaced,totalComm:result.totalComm||0,locked:false};
      const newRecords=[...records,rec];
      setTrades(newTrades);setRecords(newRecords);
      await syncToServer(newTrades,newRecords);
      if(replaced>0) addLog(`✅ Added ${added} new · replaced ${replaced} updated positions`,'ok');
      else addLog(`✅ Imported ${added} positions`,'ok');
    }catch(e){addLog(`❌ ${e.message}`,'err');}
  },[trades,records,syncToServer]);

  const handleFile=useCallback(async(file)=>{
    if(!file)return;setLogVisible(true);
    const log=[];const addLog=(msg,cls='info')=>{log.push({msg,cls});setImportLog([...log]);};
    addLog(`📄 ${file.name}`);
    const isXml = /\.xml$/i.test(file.name) || file.type==='text/xml' || file.type==='application/xml';
    const isHtm = /\.html?$/i.test(file.name) || file.type==='text/html';
    // XML = Flex Query downloaded directly from IBKR — reuse the same handleFlexXml pipeline
    if(isXml){ const text=await file.text(); await handleFlexXml(text); return; }
    try{
      let result;
      if(isHtm){
        addLog('   Format: HTM ⭐ (exact commissions, clean dates)');
        const text=await file.text();
        result=parseHTM(text,msg=>addLog('   '+msg));
      } else {
        addLog('   Format: PDF · tip: use HTM for better accuracy');
        const pdfjsLib=await ensurePdfJs();
        const buf=await file.arrayBuffer();const pdf=await pdfjsLib.getDocument({data:buf}).promise;
        addLog(`   ${pdf.numPages} pages`);
        let text='';for(let i=1;i<=pdf.numPages;i++){const pg=await pdf.getPage(i);const ct=await pg.getTextContent();text+=ct.items.map(it=>it.str).join(' ')+'\n';}
        result=parsePDF(text,msg=>addLog('   '+msg));
      }
      if(!result){addLog('❌ Parse failed','err');return;}

      // ── Replace-by-open-date dedup ─────────────────────────────────────────
      // A leg is identified by (account, OCC code, openDate). When a newer
      // import has the same identifier, it REPLACES the older trade — newer
      // data is always more complete (extended close dates, combined P&L for
      // re-traded contracts, etc.). This fixes the daily-import P&L drift.
      const openKey = (leg) => `${leg.account||''}|${leg.contract||leg.key||''}|${leg.tradeDate||''}`;
      const existingOpenKeys = new Map(); // openKey → tradeIndex
      trades.forEach((t, idx) => {
        (t.legs || [t]).forEach(l => existingOpenKeys.set(openKey(l), idx));
      });

      const replacedIdx = new Set();
      let added=0, replaced=0;
      const incoming = [];
      for(const t of result.trades){
        const legs = t.legs || [t];
        const matches = legs.map(l => existingOpenKeys.get(openKey(l)))
                            .filter(i => i !== undefined);
        if(matches.length > 0){
          matches.forEach(i => replacedIdx.add(i));
          replaced++;
        } else {
          added++;
        }
        incoming.push(t);
      }

      const recId = Date.now();
      const kept = trades.filter((_, i) => !replacedIdx.has(i));
      const newTrades = [...kept, ...incoming.map(t => ({...t, importId: recId}))];
      const rec={id:recId,filename:file.name,account:result.account,name:result.name,
        importedAt:new Date().toISOString(),found:result.trades.length,added,replaced,
        totalComm:result.totalComm||0,locked:false};
      const newRecords=[...records,rec];setTrades(newTrades);setRecords(newRecords);
      await syncToServer(newTrades,newRecords);
      if(replaced > 0) addLog(`✅ Added ${added} new · replaced ${replaced} updated trades`,'ok');
      else addLog(`✅ Added ${added}`,'ok');
    }catch(e){addLog(`❌ ${e.message}`,'err');}
  },[trades,records,syncToServer]);

  const handleDrop=useCallback(e=>{
    e.preventDefault();
    const f=e.dataTransfer.files[0];
    if(f&&(/\.xml$/i.test(f.name)||f.type==='text/xml'||f.type==='application/xml'||/\.html?$/i.test(f.name)||f.type==='text/html'||f.type==='application/pdf'||/\.pdf$/i.test(f.name)))handleFile(f);
  },[handleFile]);

  const syncLabel={loading:'⟳ Loading…',idle:'',syncing:'⟳ Syncing…',ok:'✓ Synced',err:'✗ Sync error'}[syncState];
  const syncColor={loading:C.wa,idle:C.mu,syncing:C.wa,ok:C.up,err:C.dn}[syncState];
  const TABS=[['overview','◎ Overview'],['calendar','📅 Calendar'],['performance','∿ Perf'],['trades','≡ Trades'],['records','⊞ Records'],['notes','📋 Session Notes'],['settings','⚙ Settings']];

  // Detect stale data: trades missing open/close dates need re-import
  // Stale = missing dates AND not a Flex/confirmation import (those can have '?' for same-day trades).
  // Only warn for legacy PDF imports where the date bug applied.
  const staleCount = trades.filter(t =>
    (!t.tradeDate || t.tradeDate === '?' || !t.closeDate || t.closeDate === '?') &&
    !t.source &&                          // Flex imports set source='confirmation'
    !t.key?.includes('_flex_') &&         // activity Flex keys contain _flex_
    !t.key?.includes('_conf_')            // confirmation Flex keys contain _conf_
  ).length;
  const staleWarning = staleCount > 0 ? (
    <div style={{background:'rgba(210,153,34,0.12)',border:'1px solid var(--warn)',borderRadius:6,padding:'8px 14px',marginBottom:12,display:'flex',alignItems:'center',gap:10,fontSize:11,fontFamily:"'IBM Plex Mono',monospace"}}>
      <span style={{color:'var(--warn)',fontWeight:700}}>⚠ {staleCount} trades missing dates</span>
      <span style={{color:'var(--muted)'}}>— imported before a bug fix. Clear all data and re-import your PDF to fix.</span>
      <button onClick={async()=>{if(!confirm('Clear ALL trade data and re-import?'))return;setTrades([]);setRecords([]);await syncToServer([],[]);switchJournalTab('settings');}}
        style={{marginLeft:'auto',background:'var(--warn)',color:'#000',border:'none',borderRadius:4,padding:'3px 10px',fontSize:10,fontWeight:700,cursor:'pointer',letterSpacing:'.05em'}}>
        CLEAR &amp; GO TO IMPORT
      </button>
    </div>
  ) : null;

  const renderPanel=()=>{
    if(syncState==='loading')return<div className="empty">Loading…</div>;
    // Trades filtered by the user's chosen start date (for overview stats)
    const overviewTrades = startDate
      ? trades.filter(t => (t.closeDate || t.tradeDate || '') >= startDate)
      : trades;
    switch(tab){
      case'notes':return<SessionNotesLogTab/>;
      case'settings':return<JournalSettingsTab
        importLog={importLog} logVisible={logVisible}
        onFile={handleFile} onDrop={handleDrop} onIBKRXml={handleFlexXml}
        startDate={startDate} onStartDate={setJournalStart}
        trades={trades} records={records}
        onClearAll={async()=>{
          if(!confirm('Clear all unlocked trade data?'))return;
          const lockedIds=new Set(records.filter(r=>r.locked).map(r=>r.id));
          const nr=records.filter(r=>r.locked);
          const nt=trades.filter(t=>lockedIds.has(t.importId));
          setTrades(nt); setRecords(nr);
          await syncToServer(nt,nr);
        }}
      />;
      case'overview':return<div>{staleWarning}<OverviewTab trades={overviewTrades} records={records} startDate={startDate}/></div>;
      case'calendar':return<CalendarTab trades={overviewTrades} regimes={regimes}/>;
      case'performance':return<div>{staleWarning}<PerformanceTab trades={trades} records={records}/></div>;
      case'trades':return<div>{staleWarning}<TradesTab trades={trades} onRemove={removeTrade} records={records} regimes={regimes}/></div>;
      case'records':return<RecordsTab trades={trades} records={records}
        onDelete={async id=>{
          const nr=records.filter(r=>r.id!==id);
          const nt=trades.filter(t=>t.importId!==id);
          setRecords(nr);setTrades(nt);
          await syncToServer(nt,nr);
        }}
        onToggleLock={async id=>{
          const nr=records.map(r=>r.id===id?{...r,locked:!r.locked}:r);
          setRecords(nr);
          await syncToServer(trades,nr);
        }}
        onClear={async()=>{
          // Keep locked records and their linked trades
          const lockedIds=new Set(records.filter(r=>r.locked).map(r=>r.id));
          const nr=records.filter(r=>r.locked);
          const nt=trades.filter(t=>lockedIds.has(t.importId));
          setTrades(nt); setRecords(nr);
          setSyncState('syncing');
          try {
            await Promise.all([journalPutTrades(nt),journalPutRecords(nr)]);
            setSyncState('ok');
          } catch { setSyncState('err'); }
        }}/>;
      default:return null;
    }};

  return(<div style={{height:'100%',display:'flex',flexDirection:'column',background:'var(--bg)',overflow:'hidden'}}>
    <div style={{padding:'7px 14px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:12,flexShrink:0,background:'var(--bg-elev)'}}>
      <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,fontWeight:700,color:'var(--accent)',letterSpacing:'.1em'}}>TRADE JOURNAL</span>
      <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:9,color:'var(--muted)'}}>{trades.length} trades</span>
      <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:9,color:syncColor,marginLeft:'auto'}}>{syncLabel}</span>
    </div>
    <div style={{display:'flex',borderBottom:'1px solid var(--border)',background:'var(--bg-elev)',flexShrink:0,overflowX:'auto'}}>
      {TABS.map(([id,label])=>(
        <button key={id} onClick={()=>switchJournalTab(id)} style={{padding:'7px 13px',background:'none',border:'none',borderBottom:tab===id?'2px solid var(--accent)':'2px solid transparent',color:tab===id?'var(--accent)':'var(--muted)',fontFamily:"'IBM Plex Mono',monospace",fontSize:10,cursor:'pointer',letterSpacing:'.06em',textTransform:'uppercase',whiteSpace:'nowrap'}}>
          {label}
        </button>))}
    </div>
    <div style={{flex:1,overflow:'auto',padding:14}}>{renderPanel()}</div>
  </div>);}
